import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabase';
import AddStudentReceptionist from './AddStudentReceptionist';
import './ReceptionistDashboard.css';

function ReceptionistDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [receptionistData, setReceptionistData] = useState(null);
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Determine active view from URL path
  const getActiveViewFromPath = (path) => {
    if (path === '/admission/add-student') return 'addStudent';
    if (path === '/admission/students-records') return 'studentsRecords';
    return 'welcome'; // Default to 'welcome' for '/admission'
  };
  
  const [activeView, setActiveView] = useState(getActiveViewFromPath(location.pathname));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [classes, setClasses] = useState([]);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [activeTab, setActiveTab] = useState('student');
  const [loadingViewData, setLoadingViewData] = useState(false);
  const [viewingSiblings, setViewingSiblings] = useState([]);
  const [viewingTeachers, setViewingTeachers] = useState([]);
  const [viewingFees, setViewingFees] = useState(null);
  const [viewingParents, setViewingParents] = useState([]);
  const [filters, setFilters] = useState({
    searchText: '',
    classId: '',
    sectionId: '',
    status: '',
    gender: ''
  });
  const [filterSections, setFilterSections] = useState([]);
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [showDeleteSuccessPopup, setShowDeleteSuccessPopup] = useState(false);
  const [deletedStudentName, setDeletedStudentName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    // Check if receptionist is authenticated
    const checkAuthentication = async () => {
      const isAuthenticated = localStorage.getItem('receptionistAuthenticated');
      const storedData = localStorage.getItem('receptionistData');

      if (!isAuthenticated || !storedData) {
        navigate('/admission/login');
        return;
      }

      try {
        const data = JSON.parse(storedData);
        setReceptionistData(data);
        
        // Fetch school data using receptionist's school_id
        if (data.school_id) {
          const { data: school, error: schoolError } = await supabase
            .from('schools')
            .select('*')
            .eq('id', data.school_id)
            .single();
          
          if (schoolError) {
            console.error('Error fetching school data:', schoolError);
          } else {
            setSchoolData(school);
            // Store school data in localStorage so AddStudent can use it
            localStorage.setItem('schoolData', JSON.stringify(school));
          }
        }
      } catch (err) {
        console.error('Error parsing receptionist data:', err);
        navigate('/admission/login');
      } finally {
        setLoading(false);
      }
    };

    checkAuthentication();
  }, [navigate]);

  // Update activeView when URL changes
  useEffect(() => {
    const view = getActiveViewFromPath(location.pathname);
    setActiveView(view);
  }, [location.pathname]);

  // Fetch students when viewing students records
    const fetchStudents = async () => {
      if (!schoolData?.id || activeView !== 'studentsRecords') return;
      
      try {
        setLoadingStudents(true);
        
        // Fetch students for the school that were added by this receptionist and are pending approval
        const { data: studentsData, error: studentsError } = await supabase
          .from('students')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('created_by_receptionist_id', receptionistData.id) // Only show students added by this receptionist
          .eq('is_approved', false) // Only show pending (not approved) students
          .order('first_name');
        
        if (studentsError) {
          console.error('Error fetching students:', studentsError);
          return;
        }
        
        // Fetch classes
        const { data: classesData, error: classesError } = await supabase
          .from('classes')
          .select('id, class_name')
          .eq('school_id', schoolData.id)
          .eq('is_active', true);
        
        if (classesError) {
          console.error('Error fetching classes:', classesError);
        }
        
        // Create a map of class_id to class_name
        const classMap = {};
        if (classesData) {
          classesData.forEach(cls => {
            classMap[cls.id] = cls.class_name;
          });
        }
        
        // Combine students with their class names
        const studentsWithClasses = (studentsData || []).map(student => ({
          ...student,
          class_name: classMap[student.class_id] || 'No Class'
        }));
        
        setStudents(studentsWithClasses);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoadingStudents(false);
      }
    };

  useEffect(() => {
    if (schoolData?.id && activeView === 'studentsRecords' && receptionistData?.id) {
      fetchStudents();
    }
  }, [schoolData?.id, activeView, receptionistData?.id]);

  // Fetch classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!schoolData?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('classes')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('class_name');
        
        if (error) {
          console.error('Error fetching classes:', error);
          return;
        }
        
        setClasses(data || []);
      } catch (err) {
        console.error('Error:', err);
      }
    };

    if (schoolData?.id) {
      fetchClasses();
    }
  }, [schoolData?.id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (err) {
      return dateString;
    }
  };

  const fetchFilterSections = async (classId) => {
    if (!schoolData?.id || !classId) {
      setFilterSections([]);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('sections')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', classId)
        .eq('is_active', true)
        .order('section_name');
      
      if (error) {
        console.error('Error fetching sections:', error);
        setFilterSections([]);
        return;
      }
      
      setFilterSections(data || []);
    } catch (err) {
      console.error('Error:', err);
      setFilterSections([]);
    }
  };

  const getValidFileUrl = async (url) => {
    if (!url) return null;
    
    try {
      let filePath = null;
      
      // Try different URL patterns to extract the file path
      // Pattern 1: Full Supabase storage URL: https://project.supabase.co/storage/v1/object/student-images/path/to/file
      const pattern1 = url.match(/\/storage\/v1\/object\/student-images\/(.+)$/);
      if (pattern1) {
        filePath = decodeURIComponent(pattern1[1]);
      }
      
      // Pattern 2: Public URL: /storage/v1/object/public/student-images/path/to/file
      const pattern2 = url.match(/\/storage\/v1\/object\/public\/student-images\/(.+)$/);
      if (pattern2) {
        filePath = decodeURIComponent(pattern2[1]);
      }
      
      // Pattern 3: Relative path: student-images/path/to/file
      const pattern3 = url.match(/student-images\/(.+)$/);
      if (pattern3) {
        filePath = decodeURIComponent(pattern3[1]);
      }
      
      // Pattern 4: Direct path (already a path, not a URL) - remove leading slash if present
      if (!filePath && !url.includes('http') && !url.includes('://')) {
        filePath = url.startsWith('/') ? url.substring(1) : url;
        // Remove 'student-images/' prefix if it exists
        if (filePath.startsWith('student-images/')) {
          filePath = filePath.substring('student-images/'.length);
        }
      }
      
      // If we found a file path, generate a signed URL
      if (filePath) {
        console.log('Extracted file path:', filePath);
        
        // Clean the file path - remove any query parameters
        const cleanPath = filePath.split('?')[0];
        
        try {
          const { data, error } = await supabase.storage
            .from('student-images')
            .createSignedUrl(cleanPath, 3600); // Valid for 1 hour
          
          if (!error && data?.signedUrl) {
            console.log('Generated signed URL successfully:', data.signedUrl);
            return data.signedUrl;
          } else {
            console.error('Error creating signed URL:', error);
            // Try public URL as fallback
            const { data: publicData } = supabase.storage
              .from('student-images')
              .getPublicUrl(cleanPath);
            if (publicData?.publicUrl) {
              console.log('Using public URL as fallback:', publicData.publicUrl);
              return publicData.publicUrl;
            }
          }
        } catch (signError) {
          console.error('Error in createSignedUrl:', signError);
          // Try public URL as fallback
          try {
            const { data: publicData } = supabase.storage
              .from('student-images')
              .getPublicUrl(cleanPath);
            if (publicData?.publicUrl) {
              return publicData.publicUrl;
            }
          } catch (publicError) {
            console.error('Error getting public URL:', publicError);
          }
        }
      }
      
      // If all else fails, return original URL
      console.log('Using original URL:', url);
      return url;
    } catch (err) {
      console.error('Error generating signed URL:', err);
      return url; // Return original URL as fallback
    }
  };

  // Helper function to get parent label based on relationship
  const getParentLabel = (parent, parentIndex, allParents) => {
    const relationship = (parent.relationship || '').trim();
    
    if (relationship === 'Mother') {
      return 'Mother';
    } else if (relationship === 'Father') {
      return 'Father';
    } else if (relationship === 'Guardian' || relationship.toLowerCase().includes('guardian')) {
      // Count guardians up to and including this parent
      let guardianNumber = 0;
      for (let i = 0; i <= parentIndex; i++) {
        const p = allParents[i];
        const rel = ((p.relationship || '') || '').trim().toLowerCase();
        if (rel === 'guardian' || rel.includes('guardian')) {
          guardianNumber++;
        }
      }
      return `Guardian ${guardianNumber}`;
    } else {
      // Fallback: if no relationship set, use Parent X
      return `Parent ${parentIndex + 1}`;
    }
  };

  const handleViewStudent = async (student) => {
    if (!schoolData?.id) return;
    
    setLoadingViewData(true);
    setActiveTab('student'); // Reset to first tab
    
    try {
      // Fetch student documents
      const { data: documentsData } = await supabase
        .from('student_documents')
        .select('*')
        .eq('student_id', student.id)
        .eq('school_id', schoolData.id)
        .is('parent_id', null) // Only fetch student documents, not parent documents
        .order('uploaded_at', { ascending: false });
      
      // Generate signed URLs for documents
      const documentsWithUrls = await Promise.all(
        (documentsData || []).map(async (doc) => {
          const validUrl = await getValidFileUrl(doc.document_url);
          return {
            ...doc,
            document_url: validUrl || doc.document_url
          };
        })
      );
      
      // Generate signed URL for emergency documents if exists
      let emergencyDocumentsUrl = null;
      if (student.emergency_documents_url) {
        emergencyDocumentsUrl = await getValidFileUrl(student.emergency_documents_url);
      }
      
      // Generate signed URL for profile picture if exists
      let profilePictureUrl = null;
      if (student.profile_picture_url) {
        try {
          profilePictureUrl = await getValidFileUrl(student.profile_picture_url);
        } catch (urlError) {
          console.error('Error generating profile picture URL:', urlError);
        }
      }
      
      // Fetch parents from parents table via student_parents junction
      let parentsData = [];
      const { data: studentParents } = await supabase
        .from('student_parents')
        .select(`
          *,
          parents:parent_id (
            id,
            name,
            email,
            phone,
            relationship,
            address,
            can_pickup,
            is_emergency_contact,
            pays_fees,
            is_primary
          )
        `)
        .eq('student_id', student.id)
        .order('is_primary', { ascending: false });
      
      if (studentParents && studentParents.length > 0) {
        parentsData = studentParents
          .map(sp => ({
            ...sp.parents,
            is_primary_for_student: sp.is_primary
          }))
          .filter(p => p !== null);
        
        // Fetch documents for each parent
        const parentsWithDocuments = await Promise.all(
          parentsData.map(async (parent) => {
            if (parent.id) {
              // Fetch parent documents
              const { data: parentDocs } = await supabase
                .from('student_documents')
                .select('*')
                .eq('student_id', student.id)
                .eq('parent_id', parent.id)
                .eq('school_id', schoolData.id)
                .order('uploaded_at', { ascending: false });
              
              // Generate signed URLs for parent documents
              const parentDocsWithUrls = await Promise.all(
                (parentDocs || []).map(async (doc) => {
                  const validUrl = await getValidFileUrl(doc.document_url);
                  return {
                    ...doc,
                    document_url: validUrl || doc.document_url
                  };
                })
              );
              
              return {
                ...parent,
                documents: parentDocsWithUrls || []
              };
            }
            return {
              ...parent,
              documents: []
            };
          })
        );
        
        parentsData = parentsWithDocuments;
      }
      
      // If no parents found in parents table, fall back to student's parent fields
      if (parentsData.length === 0 && (student.parent_name || student.parent_email)) {
        parentsData = [{
          name: student.parent_name || 'N/A',
          email: student.parent_email || 'N/A',
          phone: student.parent_phone || 'N/A',
          relationship: student.parent_relationship || 'N/A',
          address: student.parent_address || student.address || 'N/A',
          is_primary_for_student: true,
          documents: []
        }];
      }
      
      // Fetch siblings (students with same parent email)
      let siblingsData = [];
      if (student.parent_email) {
        const { data: siblings } = await supabase
          .from('students')
          .select(`
            *,
            classes:class_id (
              id,
              class_name
            )
          `)
          .eq('school_id', schoolData.id)
          .eq('parent_email', student.parent_email)
          .eq('is_active', true)
          .neq('id', student.id);
        
        siblingsData = siblings || [];
      }
      
      // Fetch teachers for the class and section
      let teachersData = [];
      if (student.class_id && student.section_id) {
        const { data: teachers } = await supabase
          .from('teachers')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true);
        
        // Filter teachers who teach this class/section
        if (teachers) {
          teachersData = teachers.filter(teacher => {
            if (teacher.classes && Array.isArray(teacher.classes)) {
              return teacher.classes.some(c => c === student.class_id);
            }
            return false;
          });
        }
      }
      
      // Fetch pickup location if transport is enabled
      let pickupLocationData = null;
      if (student.transport_service_enabled && student.pickup_location_id) {
        const { data: location } = await supabase
          .from('pickup_locations')
          .select('*')
          .eq('id', student.pickup_location_id)
          .single();
        
        if (location) {
          // Fetch route information if route_id exists
          if (student.route_id) {
            const { data: route } = await supabase
              .from('routes')
              .select(`
                *,
                transport_vehicles:vehicle_id (
                  id,
                  bus_number,
                  driver_name
                )
              `)
              .eq('id', student.route_id)
              .single();
            
            if (route) {
              pickupLocationData = {
                ...location,
                route: {
                  ...route,
                  vehicle: route.transport_vehicles || null
                }
              };
            } else {
              pickupLocationData = location;
            }
          } else {
            pickupLocationData = location;
          }
        }
      }
      
      // Set viewing student with all fetched data
      setViewingStudent({
        ...student,
        documents: documentsWithUrls || [],
        emergency_documents_url: emergencyDocumentsUrl || student.emergency_documents_url,
        profile_picture_url: profilePictureUrl || student.profile_picture_url,
        pickupLocation: pickupLocationData
      });
      
      setViewingSiblings(siblingsData);
      setViewingTeachers(teachersData);
      setViewingParents(parentsData);
    } catch (error) {
      console.error('Error fetching student details:', error);
      setViewingStudent(student); // Set with basic data if fetch fails
      setViewingParents([]);
    } finally {
      setLoadingViewData(false);
    }
  };

  const [editForm, setEditForm] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    nationality: '',
    classId: '',
    sectionId: '',
    dateOfBirth: '',
    gender: '',
    admissionDate: '',
    previousSchool: '',
    previousSchoolGrade: '',
    studentIdNo: '',
    parents: [
      {
        id: null,
        name: '',
        email: '',
        phone: '',
        relationship: '',
        primaryContact: 'primary',
        address: '',
        canPickup: false,
        isEmergencyContact: false,
        paysFees: false,
        useExisting: false,
        existingParentId: null
      }
    ],
    emergencyContact: '',
    emergencyPhone: '',
    address: '',
    profilePicture: null,
    medicalDetails: '',
    emergencyDocuments: null,
    emergencyDocumentsUrl: '',
      transportServiceEnabled: false,
      pickupLocationId: '',
      useStudentAddressAsPickup: false,
      transportRouteType: '',
      transportTime: '',
      routeId: '',
    paymentMethod: '',
    documents: [],
    newDocuments: [],
    studentPassword: '',
    studentConfirmPassword: ''
  });
  const [editErrors, setEditErrors] = useState({});
  
  // Country code states for edit form
  const [receptionistEditStudentPhoneCountryCode, setReceptionistEditStudentPhoneCountryCode] = useState('+92');
  const [receptionistEditParentPhoneCountryCodes, setReceptionistEditParentPhoneCountryCodes] = useState(['+92']);
  const [receptionistEditEmergencyPhoneCountryCode, setReceptionistEditEmergencyPhoneCountryCode] = useState('+92');
  
  // Helper function to extract country code from phone number
  const extractCountryCode = (phoneNumber) => {
    if (!phoneNumber) return '+92';
    const countryCodes = ['+971', '+966', '+974', '+965', '+973', '+968', '+880', '+977', '+358', '+234', '+254', '+212', '+967', '+964', '+963', '+962', '+961', '+98', '+972', '+92', '+91', '+90', '+86', '+84', '+82', '+81', '+66', '+65', '+64', '+63', '+62', '+61', '+60', '+57', '+55', '+54', '+52', '+49', '+47', '+46', '+45', '+44', '+43', '+41', '+39', '+34', '+33', '+32', '+31', '+27', '+20', '+7', '+1'];
    for (const code of countryCodes) {
      if (phoneNumber.startsWith(code)) {
        return code;
      }
    }
    return '+92';
  };
  
  // Helper function to remove country code from phone number
  const removeCountryCode = (phoneNumber) => {
    if (!phoneNumber) return '';
    const countryCodes = ['+971', '+966', '+974', '+965', '+973', '+968', '+880', '+977', '+358', '+234', '+254', '+212', '+967', '+964', '+963', '+962', '+961', '+98', '+972', '+92', '+91', '+90', '+86', '+84', '+82', '+81', '+66', '+65', '+64', '+63', '+62', '+61', '+60', '+57', '+55', '+54', '+52', '+49', '+47', '+46', '+45', '+44', '+43', '+41', '+39', '+34', '+33', '+32', '+31', '+27', '+20', '+7', '+1'];
    for (const code of countryCodes) {
      if (phoneNumber.startsWith(code)) {
        return phoneNumber.substring(code.length).trim();
      }
    }
    return phoneNumber;
  };
  
  const [editSections, setEditSections] = useState([]);
  const [loadingEditSections, setLoadingEditSections] = useState(false);
  const [editPickupLocations, setEditPickupLocations] = useState([]);
  const [loadingEditPickupLocations, setLoadingEditPickupLocations] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [editSiblings, setEditSiblings] = useState([]);
  const [loadingEditSiblings, setLoadingEditSiblings] = useState(false);
  const [editTeachers, setEditTeachers] = useState([]);
  const [loadingEditTeachers, setLoadingEditTeachers] = useState(false);
  const [originalDocuments, setOriginalDocuments] = useState([]);

  const handleEditStudent = async (student) => {
    setEditingStudent(student);
    
    // Generate signed URLs for profile picture and emergency documents
    let profilePictureUrl = student.profile_picture_url || '';
    let emergencyDocumentsUrl = student.emergency_documents_url || '';
    
    if (student.profile_picture_url) {
      try {
        profilePictureUrl = await getValidFileUrl(student.profile_picture_url) || student.profile_picture_url;
      } catch (err) {
        console.error('Error generating profile picture URL for edit:', err);
      }
    }
    
    if (student.emergency_documents_url) {
      try {
        emergencyDocumentsUrl = await getValidFileUrl(student.emergency_documents_url) || student.emergency_documents_url;
      } catch (err) {
        console.error('Error generating emergency documents URL for edit:', err);
      }
    }
    
    // Format dates for input fields (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };
    
    // Extract country codes from phone numbers
    const studentPhoneCode = extractCountryCode(student.phone || '');
    const emergencyPhoneCode = extractCountryCode(student.emergency_phone || '');
    
    setReceptionistEditStudentPhoneCountryCode(studentPhoneCode);
    setReceptionistEditEmergencyPhoneCountryCode(emergencyPhoneCode);
    
    // Fetch parents from parents table via student_parents junction
    let parentsData = [];
    const { data: studentParents } = await supabase
      .from('student_parents')
      .select(`
        *,
        parents:parent_id (
          id,
          name,
          email,
          phone,
          relationship,
          address,
          can_pickup,
          is_emergency_contact,
          pays_fees,
          is_primary
        )
      `)
      .eq('student_id', student.id)
      .order('is_primary', { ascending: false });
    
    if (studentParents && studentParents.length > 0) {
      parentsData = studentParents
        .map(sp => ({
          id: sp.parents?.id || null,
          name: sp.parents?.name || '',
          email: sp.parents?.email || '',
          phone: sp.parents?.phone || '',
          relationship: sp.parents?.relationship || '',
          primaryContact: sp.is_primary ? 'primary' : 'secondary',
          address: sp.parents?.address || '',
          canPickup: sp.parents?.can_pickup || false,
          isEmergencyContact: sp.parents?.is_emergency_contact || false,
          paysFees: sp.parents?.pays_fees || false,
          useExisting: !!sp.parents?.id,
          existingParentId: sp.parents?.id || null,
          documents: [] // Will be populated below
        }))
        .filter(p => p !== null);
      
      // Fetch parent documents for each parent
      for (let i = 0; i < parentsData.length; i++) {
        const parent = parentsData[i];
        if (parent.id) {
          try {
            const { data: parentDocs } = await supabase
              .from('student_documents')
              .select('*')
              .eq('parent_id', parent.id)
              .in('document_type', ['Parent National ID', 'Parent Passport'])
              .order('uploaded_at', { ascending: false });
            
            if (parentDocs && parentDocs.length > 0) {
              // Get valid URLs for documents
              const docsWithUrls = await Promise.all(
                parentDocs.map(async (doc) => {
                  let documentUrl = doc.document_url;
                  if (doc.document_url) {
                    try {
                      // Try to get signed URL if it's a private file
                      const { data: signedData } = await supabase.storage
                        .from('student-images')
                        .createSignedUrl(doc.document_url.replace(/^.*\/student-images\//, ''), 3600);
                      if (signedData?.signedUrl) {
                        documentUrl = signedData.signedUrl;
                      } else {
                        // Try public URL
                        const { data: { publicUrl } } = supabase.storage
                          .from('student-images')
                          .getPublicUrl(doc.document_url.replace(/^.*\/student-images\//, ''));
                        if (publicUrl) documentUrl = publicUrl;
                      }
                    } catch (err) {
                      console.error('Error generating document URL:', err);
                    }
                  }
                  return { ...doc, document_url: documentUrl };
                })
              );
              parentsData[i].documents = docsWithUrls;
            }
          } catch (err) {
            console.error(`Error fetching documents for parent ${parent.id}:`, err);
          }
        }
      }
    }
    
    // If no parents found in parents table, fall back to student's parent fields
    if (parentsData.length === 0 && (student.parent_name || student.parent_email)) {
      const parentPhoneCode = extractCountryCode(student.parent_phone || '');
      parentsData = [{
        id: null,
        name: student.parent_name || '',
        email: student.parent_email || '',
        phone: removeCountryCode(student.parent_phone || ''),
        relationship: student.parent_relationship || '',
        primaryContact: student.parent_is_primary ? 'primary' : 'secondary',
        address: student.parent_address || student.address || '',
        canPickup: student.parent_can_pickup || false,
        isEmergencyContact: student.parent_is_emergency_contact || false,
        paysFees: student.parent_pays_fees || false,
        useExisting: false,
        existingParentId: null
      }];
      // Set parent phone country code for the single parent
      setReceptionistEditParentPhoneCountryCodes([parentPhoneCode]);
    } else {
      // Set parent phone country codes for all parents
      const parentPhoneCodes = parentsData.map(p => extractCountryCode(p.phone || ''));
      setReceptionistEditParentPhoneCountryCodes(parentPhoneCodes);
    }
    
    setEditForm({
      firstName: student.first_name || '',
      middleName: student.middle_name || '',
      lastName: student.last_name || '',
      email: student.email || '',
      phone: removeCountryCode(student.phone || ''),
      nationality: student.nationality || '',
      classId: student.class_id ? student.class_id.toString() : '',
      sectionId: student.section_id || '',
      dateOfBirth: formatDateForInput(student.date_of_birth),
      gender: student.gender || '',
      admissionDate: formatDateForInput(student.admission_date),
      previousSchool: student.previous_school || '',
      previousSchoolGrade: student.previous_school_grade || '',
      studentIdNo: student.student_id_no || '',
      parents: parentsData.length > 0 ? parentsData : [
        {
          id: null,
          name: '',
          email: '',
          phone: '',
          relationship: '',
          primaryContact: 'primary',
          address: '',
          canPickup: false,
          isEmergencyContact: false,
          paysFees: false,
          useExisting: false,
          existingParentId: null,
          documents: []
        }
      ],
      emergencyContact: student.emergency_contact || '',
      emergencyPhone: removeCountryCode(student.emergency_phone || ''),
      address: student.address || '',
      profilePicture: null,
      medicalDetails: student.medical_details || '',
      emergencyDocuments: null,
      emergencyDocumentsUrl: emergencyDocumentsUrl,
      transportServiceEnabled: student.transport_service_enabled || false,
      pickupLocationId: student.pickup_location_id ? student.pickup_location_id.toString() : '',
      useStudentAddressAsPickup: false, // Will be determined based on pickup location
      transportRouteType: student.transport_route_type || '',
      transportTime: student.transport_time || '',
      routeId: student.route_id ? student.route_id.toString() : '',
      paymentMethod: student.payment_method || '',
      documents: [],
      newDocuments: [],
      studentPassword: '',
      studentConfirmPassword: ''
    });
    
    setEditErrors({});
    
    // Fetch sections for the selected class
    if (student.class_id) {
      fetchEditSectionsForClass(student.class_id);
    }
    
    // Fetch pickup locations
    fetchEditPickupLocations();
    
    // Fetch siblings
    fetchEditSiblings(student.parent_email, student.id);
    
    // Fetch teachers
    if (student.class_id && student.section_id) {
      fetchEditTeachers(student.class_id, student.section_id);
    }
    
    // Fetch student documents
    const fetchStudentDocuments = async () => {
      try {
        const { data: documentsData } = await supabase
          .from('student_documents')
          .select('*')
          .eq('student_id', student.id)
          .is('parent_id', null) // Only fetch student documents, not parent documents
          .order('uploaded_at', { ascending: false });
        
        // Generate signed URLs for documents
        const documentsWithUrls = await Promise.all(
          (documentsData || []).map(async (doc) => {
            let documentUrl = doc.document_url;
            if (doc.document_url) {
              try {
                documentUrl = await getValidFileUrl(doc.document_url) || doc.document_url;
              } catch (err) {
                console.error('Error generating document URL:', err);
              }
            }
            return {
              ...doc,
              document_url: documentUrl
            };
          })
        );
        
        setEditForm(prev => ({
          ...prev,
          documents: documentsWithUrls || []
        }));
        // Store original documents for comparison
        setOriginalDocuments(documentsWithUrls || []);
      } catch (err) {
        console.error('Error fetching documents:', err);
        setEditForm(prev => ({
          ...prev,
          documents: []
        }));
        setOriginalDocuments([]);
      }
    };
    
    fetchStudentDocuments();
  };

  const fetchEditSectionsForClass = async (classId) => {
    if (!schoolData?.id || !classId) {
      setEditSections([]);
      return;
    }
    
    try {
      setLoadingEditSections(true);
      const { data, error } = await supabase
        .from('sections')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', classId)
        .eq('is_active', true)
        .order('section_name');
      
      if (error) {
        console.error('Error fetching sections:', error);
        setEditSections([]);
        return;
      }
      
      setEditSections(data || []);
    } catch (err) {
      console.error('Error:', err);
      setEditSections([]);
    } finally {
      setLoadingEditSections(false);
    }
  };

  const fetchEditSiblings = async (parentEmail, currentStudentId) => {
    if (!schoolData?.id || !parentEmail) {
      setEditSiblings([]);
      return;
    }
    
    try {
      setLoadingEditSiblings(true);
      const { data: siblings, error } = await supabase
        .from('students')
        .select(`
          *,
          classes:class_id (
            id,
            class_name
          )
        `)
        .eq('school_id', schoolData.id)
        .eq('parent_email', parentEmail)
        .eq('is_active', true)
        .neq('id', currentStudentId);
      
      if (error) {
        console.error('Error fetching siblings:', error);
        setEditSiblings([]);
        return;
      }
      
      setEditSiblings(siblings || []);
    } catch (err) {
      console.error('Error:', err);
      setEditSiblings([]);
    } finally {
      setLoadingEditSiblings(false);
    }
  };

  const fetchEditTeachers = async (classId, sectionId) => {
    if (!schoolData?.id || !classId || !sectionId) {
      setEditTeachers([]);
      return;
    }
    
    try {
      setLoadingEditTeachers(true);
      const { data: teachers, error } = await supabase
        .from('teachers')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);
      
      if (error) {
        console.error('Error fetching teachers:', error);
        setEditTeachers([]);
        return;
      }
      
      // Filter teachers who teach this class/section
      if (teachers) {
        const filteredTeachers = teachers.filter(teacher => {
          if (teacher.classes && Array.isArray(teacher.classes)) {
            return teacher.classes.some(c => c === classId || c === parseInt(classId));
          }
          return false;
        });
        setEditTeachers(filteredTeachers);
      } else {
        setEditTeachers([]);
      }
    } catch (err) {
      console.error('Error:', err);
      setEditTeachers([]);
    } finally {
      setLoadingEditTeachers(false);
    }
  };

  const fetchEditPickupLocations = async () => {
    if (!schoolData?.id) return;
    
    try {
      setLoadingEditPickupLocations(true);
      const { data: locations, error } = await supabase
        .from('pickup_locations')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('location_name');
      
      if (error) {
        console.error('Error fetching pickup locations:', error);
        setEditPickupLocations([]);
        return;
      }
      
      // Fetch routes for each pickup location
      const locationsWithRoutes = await Promise.all(
        (locations || []).map(async (location) => {
          let routes = [];
          
          // Fetch routes that use this pickup location
          const { data: routeLocations, error: routeError } = await supabase
            .from('route_pickup_locations')
            .select('route_id')
            .eq('pickup_location_id', location.id);
          
          if (!routeError && routeLocations && routeLocations.length > 0) {
            const routeIds = routeLocations.map(rl => rl.route_id);
            
            const { data: routesData, error: routesError } = await supabase
              .from('transport_routes')
              .select('*')
              .in('id', routeIds)
              .eq('school_id', schoolData.id)
              .eq('is_active', true);
            
            if (!routesError && routesData) {
              // Fetch vehicles for each route
              const routesWithVehicles = await Promise.all(
                routesData.map(async (route) => {
                  let vehicleData = null;
                  if (route.vehicle_id) {
                    const { data: vehicle } = await supabase
                      .from('transport_vehicles')
                      .select('*')
                      .eq('id', route.vehicle_id)
                      .single();
                    vehicleData = vehicle;
                  }
                  
                  return {
                    id: route.id,
                    route_type: route.route_type,
                    route_name: route.route_name,
                    vehicle: vehicleData || null
                  };
                })
              );
              
              routes = routesWithVehicles;
            }
          }
          
          return {
            ...location,
            routes: routes
          };
        })
      );
      
      setEditPickupLocations(locationsWithRoutes);
    } catch (err) {
      console.error('Error:', err);
      setEditPickupLocations([]);
    } finally {
      setLoadingEditPickupLocations(false);
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditForm(prev => {
      const updatedForm = {
        ...prev,
        [field]: value
      };
      
      // Clear error for this field
      if (editErrors[field]) {
        setEditErrors(prevErrors => ({
          ...prevErrors,
          [field]: ''
        }));
      }
      
      // If class is changed, fetch sections
      if (field === 'classId') {
        if (value) {
          fetchEditSectionsForClass(value);
          // Fetch teachers if section is also selected
          if (prev.sectionId) {
            fetchEditTeachers(value, prev.sectionId);
          } else {
            setEditTeachers([]);
          }
        } else {
          setEditSections([]);
          setEditTeachers([]);
          updatedForm.sectionId = '';
        }
      }
      
      // If section is changed, fetch teachers
      if (field === 'sectionId') {
        if (value && prev.classId) {
          fetchEditTeachers(prev.classId, value);
        } else {
          setEditTeachers([]);
        }
      }
      
      // If parent email is changed, fetch siblings
      if (field === 'parentEmail') {
        if (value && editingStudent) {
          fetchEditSiblings(value, editingStudent.id);
        } else {
          setEditSiblings([]);
        }
      }
      
      return updatedForm;
    });
  };

  // Handle parent document add in edit mode
  const handleEditParentDocumentAdd = (parentIndex, documentType, file) => {
    if (!file || !documentType) return;
    
    // Validate file size (max 10MB per document)
    if (file.size > 10 * 1024 * 1024) {
      alert(`File size must be less than 10MB for ${documentType}`);
      return;
    }
    
    setEditForm(prev => {
      const updatedParents = [...prev.parents];
      const parent = updatedParents[parentIndex];
      
      // Remove existing document of same type if exists
      const existingDocs = (parent.documents || []).filter(doc => doc.document_type !== documentType);
      
      updatedParents[parentIndex] = {
        ...parent,
        documents: [...existingDocs, { type: documentType, file: file, url: null, fileName: file.name }]
      };
      
      return {
        ...prev,
        parents: updatedParents
      };
    });
  };

  // Handle parent document remove in edit mode
  const handleEditParentDocumentRemove = (parentIndex, documentType) => {
    setEditForm(prev => {
      const updatedParents = [...prev.parents];
      const parent = updatedParents[parentIndex];
      
      updatedParents[parentIndex] = {
        ...parent,
        documents: (parent.documents || []).filter(doc => doc.document_type !== documentType)
      };
      
      return {
        ...prev,
        parents: updatedParents
      };
    });
  };

  // Handle parent field changes in edit mode
  const handleEditParentChange = (parentIndex, field, value) => {
    setEditForm(prev => {
      const updatedParents = [...prev.parents];
      updatedParents[parentIndex] = {
        ...updatedParents[parentIndex],
        [field]: value
      };
      
      // If parent email is changed, fetch siblings
      if (field === 'email' && value && editingStudent) {
        fetchEditSiblings(value, editingStudent.id);
      }
      
      return {
        ...prev,
        parents: updatedParents
      };
    });
    
    // Clear error for this field
    const errorKey = `parents.${parentIndex}.${field}`;
    if (editErrors[errorKey]) {
      setEditErrors(prevErrors => ({
        ...prevErrors,
        [errorKey]: ''
      }));
    }
  };

  // Add a new parent in edit mode
  const addEditParent = () => {
    setEditForm(prev => ({
      ...prev,
      parents: [
        ...(prev.parents || []),
        {
          id: null,
          name: '',
          email: '',
          phone: '',
          relationship: '',
          primaryContact: 'secondary',
          address: '',
          canPickup: false,
          isEmergencyContact: false,
          paysFees: false,
          useExisting: false,
          existingParentId: null,
          documents: []
        }
      ]
    }));
    setReceptionistEditParentPhoneCountryCodes(prev => [...(prev || ['+92']), '+92']);
  };

  // Remove a parent in edit mode
  const removeEditParent = (parentIndex) => {
    setEditForm(prev => ({
      ...prev,
      parents: (prev.parents || []).filter((_, index) => index !== parentIndex)
    }));
    setReceptionistEditParentPhoneCountryCodes(prev => (prev || []).filter((_, index) => index !== parentIndex));
  };

  const handleEditDocumentAdd = (documentType, file) => {
    if (!file || !documentType) return;
    
    // Validate file size (max 10MB per document)
    if (file.size > 10 * 1024 * 1024) {
      alert(`File size must be less than 10MB for ${documentType}`);
      return;
    }
    
    setEditForm(prev => ({
      ...prev,
      newDocuments: [...prev.newDocuments, { type: documentType, file: file }]
    }));
  };

  const handleEditDocumentRemove = (index, isNew = false) => {
    if (isNew) {
      setEditForm(prev => ({
        ...prev,
        newDocuments: prev.newDocuments.filter((_, i) => i !== index)
      }));
    } else {
      // Remove from existing documents
      setEditForm(prev => ({
        ...prev,
        documents: prev.documents.filter((_, i) => i !== index)
      }));
    }
  };

  const handleEditDocumentTypeChange = (index, newType, isNew = false) => {
    if (isNew) {
      setEditForm(prev => ({
        ...prev,
        newDocuments: prev.newDocuments.map((doc, i) => 
          i === index ? { ...doc, type: newType } : doc
        )
      }));
    }
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.firstName.trim()) errors.firstName = 'First name is required';
    if (!editForm.lastName.trim()) errors.lastName = 'Last name is required';
    if (!editForm.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) errors.email = 'Invalid email format';
    if (!editForm.phone.trim()) errors.phone = 'Phone is required';
    // Class and Section validation removed - not shown in edit popup for receptionists
    // if (!editForm.classId) errors.classId = 'Class is required';
    // if (!editForm.sectionId) errors.sectionId = 'Section is required';
    if (!editForm.dateOfBirth) errors.dateOfBirth = 'Date of birth is required';
    if (!editForm.gender) errors.gender = 'Gender is required';
    if (!editForm.nationality.trim()) errors.nationality = 'Nationality is required';
    if (!editForm.admissionDate) errors.admissionDate = 'Admission date is required';
    // Validate parents array
    if (!editForm.parents || editForm.parents.length === 0) {
      errors.parents = 'At least one parent is required';
    } else {
      editForm.parents.forEach((parent, index) => {
        if (!parent.name?.trim()) {
          errors[`parents.${index}.name`] = 'Parent name is required';
        }
        if (!parent.email?.trim()) {
          errors[`parents.${index}.email`] = 'Parent email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parent.email)) {
          errors[`parents.${index}.email`] = 'Invalid email format';
        }
        if (!parent.phone?.trim()) {
          errors[`parents.${index}.phone`] = 'Parent phone is required';
        }
        if (!parent.relationship?.trim()) {
          errors[`parents.${index}.relationship`] = 'Relationship is required';
        }
      });
    }
    if (!editForm.address.trim()) errors.address = 'Address is required';
    if (!editForm.emergencyContact.trim()) errors.emergencyContact = 'Emergency contact is required';
    if (!editForm.emergencyPhone.trim()) errors.emergencyPhone = 'Emergency phone is required';
    
    // Password validation (only if passwords are provided)
    if (editForm.studentPassword && editForm.studentPassword !== editForm.studentConfirmPassword) {
      errors.studentConfirmPassword = 'Passwords do not match';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }
    
    setIsSubmittingEdit(true);
    
    try {
      let profilePictureUrl = editingStudent.profile_picture_url;
      let emergencyDocumentsUrl = editForm.emergencyDocumentsUrl;
      
      // Upload new profile picture if provided
      if (editForm.profilePicture) {
        const fileExt = editForm.profilePicture.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `student-profiles/${schoolData.id}/${fileName}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('student-images')
          .upload(filePath, editForm.profilePicture, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Profile picture upload error:', uploadError);
          alert('Failed to upload profile picture. Please try again.');
          setIsSubmittingEdit(false);
          return;
        }
        
        const { data: { publicUrl } } = supabase.storage
          .from('student-images')
          .getPublicUrl(filePath);
        
        profilePictureUrl = publicUrl;
      }
      
      // Upload new emergency documents if provided
      if (editForm.emergencyDocuments) {
        const fileExt = editForm.emergencyDocuments.name.split('.').pop();
        const fileName = `emergency_docs_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `emergency-documents/${schoolData.id}/${fileName}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('student-images')
          .upload(filePath, editForm.emergencyDocuments, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Emergency documents upload error:', uploadError);
          alert('Failed to upload emergency documents. Please try again.');
          setIsSubmittingEdit(false);
          return;
        }
        
        const { data: { publicUrl } } = supabase.storage
          .from('student-images')
          .getPublicUrl(filePath);
        
        emergencyDocumentsUrl = publicUrl;
      }
      
      // Prepare update data
      // Convert empty strings to null for bigint fields to avoid "invalid input syntax for type bigint" error
      const updateData = {
        first_name: editForm.firstName.trim(),
        middle_name: editForm.middleName.trim() || null,
        last_name: editForm.lastName.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim() ? `${receptionistEditStudentPhoneCountryCode} ${editForm.phone.trim()}` : '',
        nationality: editForm.nationality.trim(),
        class_id: editForm.classId && editForm.classId.trim() !== '' ? parseInt(editForm.classId, 10) : (editingStudent.class_id || null),
        section_id: editForm.sectionId && editForm.sectionId.trim() !== '' ? editForm.sectionId.trim() : (editingStudent.section_id || null),
        date_of_birth: editForm.dateOfBirth,
        gender: editForm.gender.trim(),
        admission_date: editForm.admissionDate,
        previous_school: editForm.previousSchool.trim() || null,
        previous_school_grade: editForm.previousSchoolGrade.trim() || null,
        student_id_no: editForm.studentIdNo.trim() || null,
        emergency_contact: editForm.emergencyContact.trim(),
        emergency_phone: editForm.emergencyPhone.trim() ? `${receptionistEditEmergencyPhoneCountryCode} ${editForm.emergencyPhone.trim()}` : '',
        address: editForm.address.trim(),
        medical_details: editForm.medicalDetails.trim() || null,
        transport_service_enabled: !!editForm.transportServiceEnabled,
        pickup_location_id: editForm.transportServiceEnabled && editForm.pickupLocationId && !editForm.useStudentAddressAsPickup && editForm.pickupLocationId.trim() !== ''
          ? parseInt(editForm.pickupLocationId, 10) 
          : null,
        transport_route_type: editForm.transportServiceEnabled && editForm.transportRouteType && editForm.transportRouteType.trim() !== ''
          ? editForm.transportRouteType 
          : null,
        transport_time: editForm.transportServiceEnabled && editForm.transportRouteType === '1 Way' && editForm.transportTime && editForm.transportTime.trim() !== ''
          ? editForm.transportTime 
          : null,
        route_id: editForm.transportServiceEnabled && editForm.routeId && editForm.routeId.trim() !== ''
          ? parseInt(editForm.routeId, 10) 
          : null,
        payment_method: editForm.paymentMethod.trim() || null,
        updated_at: new Date().toISOString()
      };
      
      // Add profile picture URL if changed
      if (profilePictureUrl !== editingStudent.profile_picture_url) {
        updateData.profile_picture_url = profilePictureUrl;
      }
      
      // Add emergency documents URL if changed
      if (emergencyDocumentsUrl !== editForm.emergencyDocumentsUrl) {
        updateData.emergency_documents_url = emergencyDocumentsUrl;
      }
      
      // Update student record
      const { data, error } = await supabase
        .from('students')
        .update(updateData)
        .eq('id', editingStudent.id)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating student:', error);
        alert(`Failed to update student: ${error.message}`);
        setIsSubmittingEdit(false);
        return;
      }
      
      // Handle multiple parents - delete existing relationships and create new ones
      if (editForm.parents && editForm.parents.length > 0 && data?.id) {
        try {
          // Delete existing student_parents relationships
          await supabase
            .from('student_parents')
            .delete()
            .eq('student_id', data.id);
          
          // Process each parent
          for (let i = 0; i < editForm.parents.length; i++) {
            const parent = editForm.parents[i];
            let parentId = null;
            
            if (parent.id) {
              // Update existing parent
              parentId = parent.id;
              const parentPhone = parent.phone.trim() ? `${receptionistEditParentPhoneCountryCodes[i] || '+92'} ${parent.phone.trim()}` : null;
              
              await supabase
                .from('parents')
                .update({
                  name: parent.name.trim(),
                  phone: parentPhone,
                  relationship: parent.relationship.trim() || null,
                  address: parent.address.trim() || null,
                  can_pickup: !!parent.canPickup,
                  is_emergency_contact: !!parent.isEmergencyContact,
                  pays_fees: !!parent.paysFees,
                  is_primary: parent.primaryContact === 'primary'
                })
                .eq('id', parentId);
            } else {
              // Create new parent or find existing by email
              const { data: existingParent } = await supabase
                .from('parents')
                .select('id, auth_user_id')
                .eq('school_id', schoolData.id)
                .eq('email', parent.email.trim().toLowerCase())
                .maybeSingle();
              
              if (existingParent) {
                // Parent already exists, use it and update
                parentId = existingParent.id;
                const parentPhone = parent.phone.trim() ? `${receptionistEditParentPhoneCountryCodes[i] || '+92'} ${parent.phone.trim()}` : null;
                
                await supabase
                  .from('parents')
                  .update({
                    name: parent.name.trim(),
                    phone: parentPhone,
                    relationship: parent.relationship.trim() || null,
                    address: parent.address.trim() || null,
                    can_pickup: !!parent.canPickup,
                    is_emergency_contact: !!parent.isEmergencyContact,
                    pays_fees: !!parent.paysFees,
                    is_primary: parent.primaryContact === 'primary'
                  })
                  .eq('id', parentId);
              } else {
                // Create new parent (without auth user for edit mode)
                const parentPhone = parent.phone.trim() ? `${receptionistEditParentPhoneCountryCodes[i] || '+92'} ${parent.phone.trim()}` : null;
                
                const { data: newParent, error: parentError } = await supabase
                  .from('parents')
                  .insert({
                    school_id: schoolData.id,
                    name: parent.name.trim(),
                    email: parent.email.trim().toLowerCase(),
                    phone: parentPhone,
                    relationship: parent.relationship.trim() || null,
                    address: parent.address.trim() || null,
                    can_pickup: !!parent.canPickup,
                    is_emergency_contact: !!parent.isEmergencyContact,
                    pays_fees: !!parent.paysFees,
                    is_primary: parent.primaryContact === 'primary'
                  })
                  .select()
                  .single();
                
                if (parentError) {
                  console.error(`Error creating parent ${i + 1}:`, parentError);
                } else {
                  parentId = newParent.id;
                }
              }
            }
            
            // Create student-parent relationship
            if (parentId) {
              await supabase
                .from('student_parents')
                .insert({
                  student_id: data.id,
                  parent_id: parentId,
                  is_primary: parent.primaryContact === 'primary'
                });
              
              // Upload parent documents if any
              if (parent.documents && parent.documents.length > 0) {
                try {
                  const parentDocumentRecords = [];
                  for (const doc of parent.documents) {
                    // Only upload new documents (those with file property)
                    if (doc.file) {
                      const fileExt = doc.file.name.split('.').pop();
                      const fileName = `${doc.type.replace(/\s+/g, '_')}_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
                      const filePath = `student-documents/${schoolData.id}/${fileName}`;
                      
                      const { error: uploadError } = await supabase.storage
                        .from('student-images')
                        .upload(filePath, doc.file);
                      
                      if (uploadError) {
                        console.error(`Error uploading ${doc.type} for parent ${i + 1}:`, uploadError);
                        continue;
                      }
                      
                      // Get public URL
                      const { data: { publicUrl } } = supabase.storage
                        .from('student-images')
                        .getPublicUrl(filePath);
                      
                      parentDocumentRecords.push({
                        student_id: data.id,
                        parent_id: parentId,
                        school_id: schoolData.id,
                        document_type: doc.type,
                        document_url: publicUrl,
                        file_name: doc.file.name,
                        file_size: doc.file.size
                      });
                    }
                  }
                  
                  if (parentDocumentRecords.length > 0) {
                    const { error: parentDocError } = await supabase
                      .from('student_documents')
                      .insert(parentDocumentRecords);
                    
                    if (parentDocError) {
                      console.error(`Error saving parent documents for parent ${i + 1}:`, parentDocError);
                    }
                  }
                } catch (parentDocErr) {
                  console.error(`Exception uploading parent documents for parent ${i + 1}:`, parentDocErr);
                }
              }
            }
          }
        } catch (parentsErr) {
          console.error('Error saving parents:', parentsErr);
          alert(`Warning: Student was updated but there was an error saving some parent information: ${parentsErr.message}`);
        }
      }
      
      // Handle document deletions - compare original documents with current documents
      const originalDocumentIds = originalDocuments.map(doc => doc.id);
      const currentDocumentIds = (editForm.documents || []).map(doc => doc.id);
      const deletedDocumentIds = originalDocumentIds.filter(id => !currentDocumentIds.includes(id));
      
      // Delete removed documents from database
      if (deletedDocumentIds.length > 0) {
        try {
          const { error: deleteError } = await supabase
            .from('student_documents')
            .delete()
            .in('id', deletedDocumentIds);
          
          if (deleteError) {
            console.error('Error deleting documents:', deleteError);
            alert(`Warning: Some documents could not be deleted: ${deleteError.message}`);
          }
        } catch (deleteErr) {
          console.error('Exception deleting documents:', deleteErr);
          alert(`Warning: Some documents could not be deleted: ${deleteErr.message}`);
        }
      }
      
      // Upload new documents if any
      if (editForm.newDocuments && editForm.newDocuments.length > 0) {
        try {
          const documentRecords = [];
          for (const doc of editForm.newDocuments) {
            if (doc.file) {
              const fileExt = doc.file.name.split('.').pop();
              const fileName = `${doc.type.replace(/\s+/g, '_')}_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
              const filePath = `student-documents/${schoolData.id}/${fileName}`;
              
              const { error: uploadError } = await supabase.storage
                .from('student-images')
                .upload(filePath, doc.file);
              
              if (uploadError) {
                console.error('Document upload error:', uploadError);
                console.error('File path:', filePath);
                console.error('File name:', doc.file.name);
                console.error('File size:', doc.file.size);
                alert(`Failed to upload ${doc.type}. Error: ${uploadError.message || JSON.stringify(uploadError)}. Please check console for details.`);
                setIsSubmittingEdit(false);
                return;
              }
              
              // Get public URL
              const { data: { publicUrl } } = supabase.storage
                .from('student-images')
                .getPublicUrl(filePath);
              
              documentRecords.push({
                student_id: data.id,
                school_id: schoolData.id,
                document_type: doc.type,
                document_url: publicUrl,
                file_name: doc.file.name,
                file_size: doc.file.size
              });
            }
          }
          
          if (documentRecords.length > 0) {
            const { error: docError } = await supabase
              .from('student_documents')
              .insert(documentRecords);
            
            if (docError) {
              console.error('Error saving documents:', docError);
              alert(`Warning: Student was updated but there was an error saving some documents: ${docError.message}`);
            }
          }
        } catch (docErr) {
          console.error('Exception uploading documents:', docErr);
          alert(`Warning: Student was updated but there was an error uploading documents: ${docErr.message}`);
        }
      }
      
      // Handle document deletions - remove documents that were in the original list but not in current list
      // This is a simple approach - in a production app, you might want to track deletions more explicitly
      
      // Update passwords if provided
      if (editForm.studentPassword) {
        // Update student auth password
        const { error: passwordError } = await supabase.auth.admin.updateUserById(
          editingStudent.auth_user_id,
          { password: editForm.studentPassword }
        );
        if (passwordError) {
          console.error('Error updating student password:', passwordError);
        }
      }
      
      
      alert('Student updated successfully!');
      closeEditPopup();
      fetchStudents(); // Refresh the list
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update student. Please try again.');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setEditErrors(prev => ({
          ...prev,
          profilePicture: 'Please select a valid image file'
        }));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setEditErrors(prev => ({
          ...prev,
          profilePicture: 'File size must be less than 5MB'
        }));
        return;
      }
      
      setEditForm(prev => ({
        ...prev,
        profilePicture: file
      }));
      
      // Clear error
      if (editErrors.profilePicture) {
        setEditErrors(prev => ({
          ...prev,
          profilePicture: ''
        }));
      }
    }
  };

  const handleEditEmergencyDocumentsChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(fileExt)) {
        setEditErrors(prev => ({
          ...prev,
          emergencyDocuments: 'Please select a valid file (PDF, DOC, DOCX, JPG, PNG)'
        }));
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setEditErrors(prev => ({
          ...prev,
          emergencyDocuments: 'File size must be less than 10MB'
        }));
        return;
      }
      
      setEditForm(prev => ({
        ...prev,
        emergencyDocuments: file
      }));
      
      // Clear error
      if (editErrors.emergencyDocuments) {
        setEditErrors(prev => ({
          ...prev,
          emergencyDocuments: ''
        }));
      }
    }
  };

  const closeEditPopup = () => {
    setEditingStudent(null);
    setOriginalDocuments([]);
    setEditForm({
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      nationality: '',
      classId: '',
      sectionId: '',
      dateOfBirth: '',
      gender: '',
      admissionDate: '',
      previousSchool: '',
      previousSchoolGrade: '',
      studentIdNo: '',
      parentName: '',
      parentEmail: '',
      parentPhone: '',
      parentRelationship: '',
      parentPrimaryContact: 'primary',
      parentAddress: '',
      parentCanPickup: false,
      parentIsEmergencyContact: false,
      parentPaysFees: false,
      emergencyContact: '',
      emergencyPhone: '',
      address: '',
      profilePicture: null,
      medicalDetails: '',
      emergencyDocuments: null,
      emergencyDocumentsUrl: '',
      transportServiceEnabled: false,
      pickupLocationId: '',
      useStudentAddressAsPickup: false,
      transportRouteType: '',
      transportTime: '',
      routeId: '',
      paymentMethod: '',
      documents: [],
      newDocuments: [],
      studentPassword: '',
      studentConfirmPassword: '',
      parentPassword: '',
      parentConfirmPassword: ''
    });
    setEditErrors({});
    setEditSections([]);
  };

  const handleDeleteStudent = (student) => {
    setStudentToDelete(student);
    setShowDeleteConfirmPopup(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirmPopup(false);
    setStudentToDelete(null);
  };

  const confirmDelete = async () => {
    if (!studentToDelete) return;

    try {
      setIsDeleting(true);
      
      // Delete the student record
      const { error } = await supabase
        .from('students')
        .delete()
        .eq('id', studentToDelete.id);
      
      if (error) {
        console.error('Error deleting student:', error);
        alert(`Failed to delete student: ${error.message}`);
        setIsDeleting(false);
        setShowDeleteConfirmPopup(false);
        setStudentToDelete(null);
        return;
      }
      
      // Store deleted student name for success popup
      const deletedName = `${studentToDelete.first_name} ${studentToDelete.middle_name ? studentToDelete.middle_name + ' ' : ''}${studentToDelete.last_name}`;
      
      // Remove from local state
      setStudents(students.filter(s => s.id !== studentToDelete.id));
      
      // Close confirmation popup
      setShowDeleteConfirmPopup(false);
      setStudentToDelete(null);
      
      // Show success popup
      setDeletedStudentName(deletedName);
      setShowDeleteSuccessPopup(true);
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to delete student');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeViewPopup = () => {
    setViewingStudent(null);
    setViewingParents([]);
    setActiveTab('student'); // Reset tab when closing
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('receptionistAuthenticated');
      localStorage.removeItem('receptionistData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/admission/login');
    } catch (err) {
      console.error('Logout error:', err);
      // Clear local storage anyway
      localStorage.removeItem('receptionistAuthenticated');
      localStorage.removeItem('receptionistData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/admission/login');
    }
  };

  if (loading) {
    return (
      <div className="receptionist-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!receptionistData) {
    return null;
  }

  // Get user's name (handle both 'name' and split name fields)
  const userName = receptionistData.name || 
    `${receptionistData.first_name || ''} ${receptionistData.last_name || ''}`.trim() || 
    'Receptionist';

  return (
    <div className="receptionist-dashboard">
      {/* Mobile Header */}
      <header className="mobile-dashboard-header">
        <div className="header-left">
          <div className="receptionist-info">
            <div className="receptionist-name-header">
              Receptionist
            </div>
          </div>
        </div>
        <div className="header-right">
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <div className="mobile-user-info">
                <div className="user-avatar">
                  {userName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="mobile-user-name">{userName}</div>
                  <div className="mobile-user-role">Receptionist</div>
                </div>
              </div>
              <button className="mobile-logout-btn" onClick={() => {
                handleLogout();
                setMobileMenuOpen(false);
              }}>
                Logout
              </button>
            </div>
            <div className="mobile-menu-tabs">
              <button 
                className={`mobile-menu-tab ${activeView === 'welcome' ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admission');
                  setMobileMenuOpen(false);
                }}
              >
                Dashboard
              </button>
              <button 
                className={`mobile-menu-tab ${activeView === 'addStudent' ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admission/add-student');
                  setMobileMenuOpen(false);
                }}
              >
                Add Student
              </button>
              <button 
                className={`mobile-menu-tab ${activeView === 'studentsRecords' ? 'active' : ''}`}
                onClick={() => {
                  navigate('/admission/students-records');
                  setMobileMenuOpen(false);
                }}
              >
                Students Records
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-layout">
        {/* Sidebar */}
        <div className={`receptionist-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-logo">
            <img 
              src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg" 
              alt="Nexy Logo" 
              className="nexy-logo-sidebar"
            />
          </div>
          
          <div className="sidebar-menu">
            <button 
              className={`sidebar-item ${activeView === 'welcome' ? 'active' : ''}`}
              onClick={() => navigate('/admission')}
              aria-label="Dashboard"
            >
              <span className="sidebar-label">Dashboard</span>
            </button>
            <button 
              className={`sidebar-item ${activeView === 'addStudent' ? 'active' : ''}`}
              onClick={() => navigate('/admission/add-student')}
              aria-label="Add Student"
            >
              <span className="sidebar-label">Add Student</span>
            </button>
            <button 
              className={`sidebar-item ${activeView === 'studentsRecords' ? 'active' : ''}`}
              onClick={() => navigate('/admission/students-records')}
              aria-label="Students Records"
            >
              <span className="sidebar-label">Students Records</span>
            </button>
          </div>

          <button 
            className="sidebar-logout"
            onClick={handleLogout}
            aria-label="Logout"
          >
            <span className="sidebar-label">Logout</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="dashboard-main-content">
          {/* Top Header Bar */}
          <div className="dashboard-header">
            <div className="header-left">
              <h1 className="page-title">
                {activeView === 'welcome' ? 'Dashboard' : 
                 activeView === 'addStudent' ? 'Add Student' : 
                 activeView === 'studentsRecords' ? 'Students Records' : 'Dashboard'}
              </h1>
            </div>
            <div className="header-right">
              <div className="user-profile">
                <div className="user-avatar">
                  {userName.charAt(0).toUpperCase()}
                </div>
                <div className="user-info">
                  <div className="user-name">{userName}</div>
                  <div className="user-role">Receptionist</div>
                </div>
              </div>
            </div>
          </div>

          {activeView === 'welcome' && (
            <div className="dashboard-content">
              {/* Welcome Card */}
              <div className="welcome-card">
                <h2>Welcome to the Admission Portal</h2>
                <p className="welcome-message">
                  You have successfully logged in as a receptionist. This is your dashboard where you can manage admissions and assist visitors.
                </p>
              </div>

              {/* Profile Info Cards */}
              <div className="profile-cards-grid">
                <div className="profile-card" style={{ borderLeftColor: '#667eea' }}>
                  <div className="profile-card-icon" style={{ background: '#EEF2FF', color: '#667eea' }}>
                    👤
                  </div>
                  <div className="profile-card-label">NAME</div>
                  <div className="profile-card-value">{userName}</div>
                </div>
                <div className="profile-card" style={{ borderLeftColor: '#FF8B3D' }}>
                  <div className="profile-card-icon" style={{ background: '#FFF7ED', color: '#FF8B3D' }}>
                    ✉️
                  </div>
                  <div className="profile-card-label">EMAIL</div>
                  <div className="profile-card-value">{receptionistData.email}</div>
                </div>
                {schoolData && (
                  <div className="profile-card" style={{ borderLeftColor: '#10B981' }}>
                    <div className="profile-card-icon" style={{ background: '#ECFDF5', color: '#10B981' }}>
                      🏫
                    </div>
                    <div className="profile-card-label">SCHOOL</div>
                    <div className="profile-card-value">{schoolData.school_name}</div>
                  </div>
                )}
                <div className="profile-card" style={{ borderLeftColor: '#8B5CF6' }}>
                  <div className="profile-card-icon" style={{ background: '#F5F3FF', color: '#8B5CF6' }}>
                    📞
                  </div>
                  <div className="profile-card-label">ROLE</div>
                  <div className="profile-card-value">Receptionist</div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'addStudent' && schoolData && (
            <div className="add-student-view">
              <AddStudentReceptionist />
            </div>
          )}


          {activeView === 'addStudent' && !schoolData && (
            <div className="dashboard-content">
              <div className="welcome-card">
                <p>Loading school data...</p>
              </div>
            </div>
          )}

          {activeView === 'studentsRecords' && (
            <div className="dashboard-content students-records-view">
              <div className="students-content">
                <div className="students-header">
                  <h1>Students Records</h1>
                  <p>View and manage all students added by you</p>
                </div>

                <div className="students-stats">
                  <div className="stat-item">
                    <span className="stat-number">{students.length}</span>
                    <span className="stat-label">Total Students</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">
                      {students.filter(student => student.is_active).length}
                    </span>
                    <span className="stat-label">Active Students</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">
                      {students.filter(student => student.gender === 'Male').length}
                    </span>
                    <span className="stat-label">Male Students</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">
                      {students.filter(student => student.gender === 'Female').length}
                    </span>
                    <span className="stat-label">Female Students</span>
                  </div>
                </div>

                {/* Filter Section */}
                {students.length > 0 && (
                  <div className="students-filter-section" style={{
                    background: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    <h3 style={{ marginBottom: '1rem', color: '#1F2937', fontSize: '1.1rem', fontWeight: '600' }}>
                      🔍 Filter Students
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem', rowGap: '1.5rem' }}>
                      {/* Search Text */}
                      <div style={{ width: '100%' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                          Search (Name/Email)
                        </label>
                        <input
                          type="text"
                          placeholder="Search by name or email..."
                          value={filters.searchText}
                          onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      {/* Class Filter */}
                      <div style={{ width: '100%' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                          Class
                        </label>
                        <select
                          value={filters.classId}
                          onChange={(e) => {
                            setFilters({ ...filters, classId: e.target.value, sectionId: '' });
                            if (e.target.value) {
                              fetchFilterSections(e.target.value);
                            } else {
                              setFilterSections([]);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            backgroundColor: '#FFFFFF'
                          }}
                        >
                          <option value="">All Classes</option>
                          {classes.map((cls) => (
                            <option key={cls.id} value={cls.id}>
                              {cls.class_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Section Filter */}
                      <div style={{ width: '100%' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                          Section
                        </label>
                        <select
                          value={filters.sectionId}
                          onChange={(e) => setFilters({ ...filters, sectionId: e.target.value })}
                          disabled={!filters.classId}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            backgroundColor: filters.classId ? '#FFFFFF' : '#F9FAFB',
                            cursor: filters.classId ? 'pointer' : 'not-allowed'
                          }}
                        >
                          <option value="">All Sections</option>
                          {filterSections.map((section) => (
                            <option key={section.id || section} value={section.id || section}>
                              {section.section_name || section}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div style={{ width: '100%' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                          Status
                        </label>
                        <select
                          value={filters.status}
                          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            backgroundColor: '#FFFFFF'
                          }}
                        >
                          <option value="">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      {/* Gender Filter */}
                      <div style={{ width: '100%' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                          Gender
                        </label>
                        <select
                          value={filters.gender}
                          onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            backgroundColor: '#FFFFFF'
                          }}
                        >
                          <option value="">All Genders</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>

                      {/* Clear Filters Button */}
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                          onClick={() => {
                            setFilters({
                              searchText: '',
                              classId: '',
                              sectionId: '',
                              status: '',
                              gender: ''
                            });
                            setFilterSections([]);
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1.5rem',
                            background: '#F3F4F6',
                            color: '#374151',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#E5E7EB';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#F3F4F6';
                          }}
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {loadingStudents ? (
                  <div className="loading-container">
                    <div className="loading-spinner">⏳</div>
                    <p>Loading students...</p>
                  </div>
                ) : students.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">👨‍🎓</div>
                    <h3>No Students Found</h3>
                    <p>You haven't added any students yet. Start by adding your first student!</p>
                    <button 
                      className="add-first-student-btn"
                      onClick={() => setActiveView('addStudent')}
                    >
                      ➕ Add Your First Student
                    </button>
                  </div>
                ) : (
                  <div className="students-table-container">
                    <table className="students-table">
                      <thead>
                        <tr>
                          <th>Student Name</th>
                          <th>Email</th>
                          <th>Class</th>
                          <th>Date of Birth</th>
                          <th>Parent Name</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Apply filters
                          let filteredStudents = students;

                          // Search filter (name or email)
                          if (filters.searchText) {
                            const searchLower = filters.searchText.toLowerCase();
                            filteredStudents = filteredStudents.filter(student => 
                              (student.first_name && student.first_name.toLowerCase().includes(searchLower)) ||
                              (student.last_name && student.last_name.toLowerCase().includes(searchLower)) ||
                              (student.email && student.email.toLowerCase().includes(searchLower))
                            );
                          }

                          // Class filter
                          if (filters.classId) {
                            filteredStudents = filteredStudents.filter(student => 
                              student.class_id === parseInt(filters.classId)
                            );
                          }

                          // Section filter
                          if (filters.sectionId) {
                            filteredStudents = filteredStudents.filter(student => 
                              student.section_id === filters.sectionId
                            );
                          }

                          // Status filter
                          if (filters.status) {
                            const isActive = filters.status === 'active';
                            filteredStudents = filteredStudents.filter(student => 
                              student.is_active === isActive
                            );
                          }

                          // Gender filter
                          if (filters.gender) {
                            filteredStudents = filteredStudents.filter(student => 
                              student.gender === filters.gender
                            );
                          }

                          return filteredStudents.length === 0 ? (
                            <tr>
                              <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                                No students found matching the selected filters.
                              </td>
                            </tr>
                          ) : (
                            filteredStudents.map((student) => (
                          <tr key={student.id}>
                            <td className="student-name">
                              {student.first_name} {student.last_name}
                            </td>
                            <td className="email-cell">
                              <a href={`mailto:${student.email}`} className="email-link">
                                {student.email}
                              </a>
                            </td>
                            <td className="class-cell">
                              <span className="class-name">
                                {student.class_name || 'No Class'}
                              </span>
                            </td>
                            <td className="dob-cell">
                              {formatDate(student.date_of_birth)}
                            </td>
                            <td className="parent-cell">
                              {student.parent_name || 'N/A'}
                            </td>
                            <td className="status-cell">
                              <span className={`status-badge ${student.is_active ? 'active' : 'inactive'}`}>
                                {student.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="actions-cell">
                              <div className="action-buttons">
                                <button 
                                  className="view-btn"
                                  title="View Student Details"
                                  onClick={() => handleViewStudent(student)}
                                >
                                  👁️
                                </button>
                                <button 
                                  className="delete-btn"
                                  title="Delete Student"
                                  onClick={() => handleDeleteStudent(student)}
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                            ))
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* View Student Details Popup - Matching School Dashboard Design */}
          {viewingStudent && (
            <div className="popup-overlay" onClick={closeViewPopup}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto', paddingBottom: '1rem' }}>
                <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
                  <h3 style={{ color: '#FFFFFF', margin: 0 }}>Student Details: {viewingStudent.first_name} {viewingStudent.last_name}</h3>
                  <button className="close-btn" onClick={closeViewPopup} style={{ color: '#FFFFFF' }}>
                    ×
                  </button>
                </div>
                
                {/* Tabs Navigation */}
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  borderBottom: '2px solid #E5E7EB',
                  backgroundColor: '#F9FAFB'
                }}>
                  {[
                    { id: 'student', label: 'Student Details' },
                    { id: 'parents', label: 'Parents Details' },
                    { id: 'siblings', label: 'Siblings Details' },
                    { id: 'teachers', label: 'Teachers Details' },
                    { id: 'medical', label: 'Medical Records' },
                    { id: 'academic', label: 'Academic Details' },
                    { id: 'transport', label: 'School Transport' },
                    { id: 'fees', label: 'Fees Payments' },
                    { id: 'documents', label: 'Documents' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        padding: '1rem 0.75rem',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: activeTab === tab.id ? '#FF8C42' : '#6B7280',
                        fontWeight: activeTab === tab.id ? '600' : '400',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        borderBottom: activeTab === tab.id ? '3px solid #FF8C42' : '3px solid transparent',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      onMouseEnter={(e) => {
                        if (activeTab !== tab.id) {
                          e.target.style.color = '#374151';
                          e.target.style.backgroundColor = '#F3F4F6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (activeTab !== tab.id) {
                          e.target.style.color = '#6B7280';
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                
                <div className="view-student-details" style={{ padding: '2rem 2rem 3rem 2rem', minHeight: '400px' }}>
                  {loadingViewData && (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <div className="loading-spinner">⏳</div>
                      <p>Loading...</p>
                    </div>
                  )}
                  
                  {!loadingViewData && (
                    <>
                      {/* Student Details Tab */}
                      {activeTab === 'student' && (
                        <div>
                          {/* Profile Picture Section */}
                          <div className="detail-section" style={{ marginBottom: '2rem', textAlign: 'center' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              Profile Picture
                            </h4>
                            <div style={{
                              padding: '2rem',
                              background: '#FFFFFF',
                              borderRadius: '12px',
                              border: '2px solid #E5E7EB',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                              position: 'relative',
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center'
                            }}>
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                              }}></div>
                              
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '0.5rem' }}>
                                {viewingStudent.profile_picture_url ? (
                                  <img 
                                    src={viewingStudent.profile_picture_url} 
                                    alt={`${viewingStudent.first_name} ${viewingStudent.last_name}`}
                                    style={{
                                      width: '200px',
                                      height: '200px',
                                      objectFit: 'cover',
                                      borderRadius: '50%',
                                      border: '4px solid #FF8C42',
                                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                    }}
                                    onLoad={async (e) => {
                                      // If image loads successfully, verify it's a valid signed URL
                                      // If not, regenerate it
                                      if (viewingStudent.profile_picture_url && !viewingStudent.profile_picture_url.includes('token=')) {
                                        const newUrl = await getValidFileUrl(viewingStudent.profile_picture_url);
                                        if (newUrl && newUrl !== viewingStudent.profile_picture_url) {
                                          e.target.src = newUrl;
                                        }
                                      }
                                    }}
                                    onError={async (e) => {
                                      console.error('Profile picture load error, attempting to regenerate URL');
                                      const originalUrl = viewingStudent.profile_picture_url;
                                      const newUrl = await getValidFileUrl(originalUrl);
                                      if (newUrl && newUrl !== originalUrl) {
                                        e.target.src = newUrl;
                                      } else {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div 
                                  style={{ 
                                    display: viewingStudent.profile_picture_url ? 'none' : 'flex',
                                    width: '200px',
                                    height: '200px',
                                    borderRadius: '50%',
                                    background: '#F3F4F6',
                                    border: '4px solid #D1D5DB',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontSize: '4rem',
                                    color: '#9CA3AF'
                                  }}
                                >
                                  👤
                                </div>
                              </div>
                              {viewingStudent.profile_picture_url && (
                                <div style={{ marginTop: '1rem' }}>
                                  <a 
                                    href={viewingStudent.profile_picture_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      const newUrl = await getValidFileUrl(viewingStudent.profile_picture_url);
                                      if (newUrl) {
                                        window.open(newUrl, '_blank');
                                      } else {
                                        alert('Unable to access profile picture. Please try again.');
                                      }
                                    }}
                                    style={{ 
                                      color: '#1F2937', 
                                      textDecoration: 'none',
                                      fontSize: '0.9rem',
                                      fontWeight: '500',
                                      padding: '0.5rem 1rem',
                                      background: '#F3F4F6',
                                      borderRadius: '8px',
                                      display: 'inline-block',
                                      cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.textDecoration = 'underline';
                                      e.target.style.background = '#E5E7EB';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.textDecoration = 'none';
                                      e.target.style.background = '#F3F4F6';
                                    }}
                                  >
                                    View Full Size →
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Personal Information */}
                  <div className="detail-section" style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                      Personal Information
                    </h4>
                    <div style={{
                      padding: '1.5rem',
                      background: '#FFFFFF',
                      borderRadius: '12px',
                      border: '2px solid #E5E7EB',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                      }}></div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                        <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Name</span>
                          <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                    {viewingStudent.first_name} {viewingStudent.middle_name} {viewingStudent.last_name}
                          </div>
                        </div>
                        <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Email</span>
                          <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                    {viewingStudent.email || 'N/A'}
                          </div>
                        </div>
                        <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Phone</span>
                                  <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                    {viewingStudent.phone || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Date of Birth</span>
                          <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                            {viewingStudent.date_of_birth ? new Date(viewingStudent.date_of_birth).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                        <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Gender</span>
                          <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                    {viewingStudent.gender || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Nationality</span>
                                  <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                    {viewingStudent.nationality || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Student ID</span>
                                  <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                    {viewingStudent.student_id_no || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Admission Date</span>
                                  <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                    {viewingStudent.admission_date ? new Date(viewingStudent.admission_date).toLocaleDateString() : 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Previous School</span>
                                  <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                    {viewingStudent.previous_school || 'N/A'}
                                  </div>
                                </div>
                                {viewingStudent.previous_school_grade && (
                                  <div>
                                    <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Previous School Grade/Percentage</span>
                                    <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                      {viewingStudent.previous_school_grade}
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Address</span>
                                  <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                    {viewingStudent.address || 'N/A'}
                                  </div>
                                </div>
                                {viewingStudent.date_of_birth && (
                                  <div>
                                    <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Age</span>
                                    <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                      {(() => {
                                        const dob = new Date(viewingStudent.date_of_birth);
                                        const today = new Date();
                                        let age = today.getFullYear() - dob.getFullYear();
                                        const monthDiff = today.getMonth() - dob.getMonth();
                                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
                                          age--;
                                        }
                                        return `${age} years`;
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Academic Information */}
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              Academic Information
                            </h4>
                            <div style={{
                              padding: '1.5rem',
                              background: '#FFFFFF',
                              borderRadius: '12px',
                              border: '2px solid #E5E7EB',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                              }}></div>
                              
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Class</span>
                                  <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                    {viewingStudent.class_name || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Section</span>
                                  <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                    {viewingStudent.section_id || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Roll Number</span>
                                  <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                    {viewingStudent.roll_number || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Status</span>
                                  <div style={{ marginTop: '0.25rem' }}>
                                    <span className={`status-badge ${viewingStudent.is_active ? 'active' : 'inactive'}`} style={{ 
                                      marginLeft: 0,
                                      padding: '0.35rem 0.75rem',
                                      borderRadius: '20px',
                                      fontSize: '0.85rem',
                                      fontWeight: '600'
                                    }}>
                                      {viewingStudent.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Parents Details Tab */}
                      {activeTab === 'parents' && (
                        <div>
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              B) Parent/Guardian Information
                            </h4>
                            {viewingParents && viewingParents.length > 0 ? (
                              <div style={{ display: 'grid', gap: '1.25rem' }}>
                                {viewingParents.map((parent, parentIndex) => (
                                  <div 
                                    key={parent.id || parentIndex} 
                                    style={{
                                      padding: '1.5rem',
                                      background: '#FFFFFF',
                                      borderRadius: '12px',
                                      border: '2px solid #E5E7EB',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                      transition: 'all 0.3s ease',
                                      position: 'relative',
                                      overflow: 'hidden'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.borderColor = '#FF8C42';
                                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 140, 66, 0.15)';
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.borderColor = '#E5E7EB';
                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                      e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                  >
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: '4px',
                                      background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                                    }}></div>
                                    
                                    {/* Parent Number Label */}
                                    <div style={{
                                      marginBottom: '1rem',
                                      paddingBottom: '0.75rem',
                                      borderBottom: '2px solid #E5E7EB'
                                    }}>
                                      <h5 style={{
                                        color: '#FF8C42',
                                        fontSize: '1.1rem',
                                        fontWeight: '700',
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                      }}>
                                        {getParentLabel(parent, parentIndex, viewingParents)}
                                        {parent.is_primary_for_student && (
                                          <span style={{
                                            padding: '0.25rem 0.75rem',
                                            backgroundColor: '#FF8C42',
                                            color: 'white',
                                            borderRadius: '20px',
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            marginLeft: 'auto'
                                          }}>
                                            Primary
                                          </span>
                                        )}
                                      </h5>
                                    </div>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                                      <div>
                                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Parent Name</span>
                                        <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                          {parent.name || 'N/A'}
                                        </div>
                                      </div>
                                      <div>
                                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Parent Email</span>
                                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                          {parent.email && parent.email !== 'N/A' ? (
                                            <a href={`mailto:${parent.email}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                                              {parent.email}
                                            </a>
                                          ) : 'N/A'}
                                        </div>
                                      </div>
                                      <div>
                                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Parent Phone</span>
                                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                          {parent.phone && parent.phone !== 'N/A' ? (
                                            <a href={`tel:${parent.phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                                              {parent.phone}
                                            </a>
                                          ) : 'N/A'}
                                        </div>
                                      </div>
                                      <div>
                                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Relationship</span>
                                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                          {parent.relationship || 'N/A'}
                                        </div>
                                      </div>
                                      <div>
                                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Parent Address</span>
                                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                          {parent.address || 'N/A'}
                                        </div>
                                      </div>
                                      <div>
                                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Additional Info</span>
                                        <div style={{ color: '#1F2937', fontSize: '0.85rem', marginTop: '0.25rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                          {parent.can_pickup && <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#E0F2FE', color: '#0369A1', borderRadius: '4px', fontSize: '0.75rem' }}>Can Pickup</span>}
                                          {parent.is_emergency_contact && <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '4px', fontSize: '0.75rem' }}>Emergency Contact</span>}
                                          {parent.pays_fees && <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: '4px', fontSize: '0.75rem' }}>Pays Fees</span>}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Parent Documents - Always Show */}
                                    <div style={{ marginTop: '1.5rem' }}>
                                      <h5 style={{ color: '#FF8C42', marginBottom: '0.75rem', fontSize: '0.95rem', fontWeight: '600', borderBottom: '1px solid #E5E7EB', paddingBottom: '0.5rem' }}>
                                        📄 Parent Documents
                                      </h5>
                                      {parent.documents && parent.documents.length > 0 ? (
                                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                                          {parent.documents.map((doc, docIndex) => (
                                            <div 
                                              key={doc.id || docIndex}
                                              style={{
                                                padding: '0.75rem',
                                                background: '#F9FAFB',
                                                borderRadius: '8px',
                                                border: '1px solid #E5E7EB',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                transition: 'all 0.2s ease'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor = '#FF8C42';
                                                e.currentTarget.style.background = '#FFF7ED';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor = '#E5E7EB';
                                                e.currentTarget.style.background = '#F9FAFB';
                                              }}
                                            >
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                                                <div style={{
                                                  width: '36px',
                                                  height: '36px',
                                                  borderRadius: '6px',
                                                  background: '#EFF6FF',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  fontSize: '1.25rem'
                                                }}>
                                                  {doc.document_type?.toLowerCase().includes('national') || doc.document_type?.toLowerCase().includes('id') ? '🆔' :
                                                   doc.document_type?.toLowerCase().includes('passport') ? '📘' :
                                                   '📄'}
                                                </div>
                                                <div>
                                                  <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '0.9rem' }}>
                                                    {doc.document_type || 'Document'}
                                                  </div>
                                                  <a 
                                                    href={doc.document_url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    onClick={async (e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      const newUrl = await getValidFileUrl(doc.document_url);
                                                      if (newUrl) {
                                                        window.open(newUrl, '_blank');
                                                      } else {
                                                        alert('Unable to access document. Please try again or contact support.');
                                                      }
                                                    }}
                                                    style={{ 
                                                      color: '#1F2937', 
                                                      textDecoration: 'none',
                                                      fontSize: '0.85rem',
                                                      fontWeight: '500'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                  >
                                                    {doc.file_name || 'View Document'} →
                                                  </a>
                                                </div>
                                              </div>
                                              {doc.file_size && (
                                                <span style={{ 
                                                  color: '#6B7280', 
                                                  fontSize: '0.75rem',
                                                  padding: '0.25rem 0.5rem',
                                                  background: '#F3F4F6',
                                                  borderRadius: '4px'
                                                }}>
                                                  {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div style={{
                                          padding: '1rem',
                                          background: '#F9FAFB',
                                          borderRadius: '8px',
                                          border: '1px dashed #D1D5DB',
                                          textAlign: 'center',
                                          color: '#6B7280',
                                          fontSize: '0.85rem',
                                          fontStyle: 'italic'
                                        }}>
                                          No documents uploaded for this parent (National ID, Passport, etc.)
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{
                                padding: '1.5rem',
                                background: '#FFFFFF',
                                borderRadius: '12px',
                                border: '2px solid #E5E7EB',
                                textAlign: 'center',
                                color: '#6B7280'
                              }}>
                                No parent information available
                              </div>
                            )}
                          </div>

                          {/* Emergency Contact Section */}
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              C) Emergency Contact Information
                            </h4>
                            <div style={{
                              padding: '1.5rem',
                              background: '#FFFFFF',
                              borderRadius: '12px',
                              border: '2px solid #E5E7EB',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                              }}></div>
                              
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Emergency Contact *</span>
                                  <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                    {viewingStudent.emergency_contact || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Emergency Phone *</span>
                                  <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                    {viewingStudent.emergency_phone && viewingStudent.emergency_phone !== 'N/A' ? (
                                      <a href={`tel:${viewingStudent.emergency_phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                                        {viewingStudent.emergency_phone}
                                      </a>
                                    ) : 'N/A'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Siblings Details Tab */}
                      {activeTab === 'siblings' && (
                        <div>
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              Siblings
                            </h4>
                            {viewingSiblings && viewingSiblings.length > 0 ? (
                              <div style={{ display: 'grid', gap: '1.25rem' }}>
                                {viewingSiblings.map((sibling) => (
                                  <div 
                                    key={sibling.id} 
                                    style={{
                                      padding: '1.5rem',
                                      background: '#FFFFFF',
                                      borderRadius: '12px',
                                      border: '2px solid #E5E7EB',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                      transition: 'all 0.3s ease',
                                      position: 'relative',
                                      overflow: 'hidden'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.borderColor = '#FF8C42';
                                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 140, 66, 0.15)';
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.borderColor = '#E5E7EB';
                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                      e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                  >
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: '4px',
                                      background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                                    }}></div>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginTop: '0.5rem' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <div>
                                          <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Name</span>
                                          <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                            {sibling.first_name} {sibling.middle_name} {sibling.last_name}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Class</span>
                          <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                            {sibling.classes?.class_name || 'N/A'} - {sibling.section_id || 'N/A'}
                                          </div>
                                        </div>
                                        {sibling.date_of_birth && (
                                          <div>
                                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Date of Birth</span>
                                            <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                              {new Date(sibling.date_of_birth).toLocaleDateString()}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <div>
                                          <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Email</span>
                                          <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                            {sibling.email || 'N/A'}
                                          </div>
                                        </div>
                                        <div>
                                          <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Gender</span>
                                          <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                            {sibling.gender || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Status</span>
                                          <div style={{ marginTop: '0.25rem' }}>
                                            <span className={`status-badge ${sibling.is_active ? 'active' : 'inactive'}`} style={{ 
                                              marginLeft: 0,
                                              padding: '0.35rem 0.75rem',
                                              borderRadius: '20px',
                                              fontSize: '0.85rem',
                                              fontWeight: '600'
                                            }}>
                                              {sibling.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ 
                                padding: '3rem 2rem', 
                                textAlign: 'center', 
                                color: '#6B7280', 
                                fontStyle: 'italic',
                                background: '#F9FAFB',
                                borderRadius: '12px',
                                border: '2px dashed #D1D5DB'
                              }}>
                                👨‍👩‍👧‍👦 No siblings found.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Teachers Details Tab */}
                      {activeTab === 'teachers' && (
                        <div>
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              Teachers
                            </h4>
                            {viewingTeachers && viewingTeachers.length > 0 ? (
                              <div style={{ display: 'grid', gap: '1.25rem' }}>
                                {viewingTeachers.map((teacher) => (
                                  <div 
                                    key={teacher.id} 
                                    style={{
                                      padding: '1.5rem',
                                      background: '#FFFFFF',
                                      borderRadius: '12px',
                                      border: '2px solid #E5E7EB',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                      transition: 'all 0.3s ease',
                                      position: 'relative',
                                      overflow: 'hidden'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.borderColor = '#FF8C42';
                                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 140, 66, 0.15)';
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.borderColor = '#E5E7EB';
                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                      e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                  >
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: '4px',
                                      background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                                    }}></div>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginTop: '0.5rem' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                        <div>
                                          <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Name</span>
                                          <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                            {teacher.first_name} {teacher.last_name}
                                          </div>
                                        </div>
                                        {teacher.email && (
                                          <div>
                                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Email</span>
                          <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                              <a href={`mailto:${teacher.email}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                                                {teacher.email}
                                              </a>
                                            </div>
                                          </div>
                                        )}
                                        {teacher.phone && (
                                          <div>
                                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Phone</span>
                                            <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                              <a href={`tel:${teacher.phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                                                {teacher.phone}
                                              </a>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                        {teacher.qualification && (
                                          <div>
                                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Qualification</span>
                                            <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                              {teacher.qualification}
                                            </div>
                                          </div>
                                        )}
                                        {teacher.subjects && Array.isArray(teacher.subjects) && teacher.subjects.length > 0 && (
                                          <div>
                                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Subjects</span>
                                            <div style={{ 
                                              color: '#1F2937', 
                                              fontSize: '0.95rem', 
                                              marginTop: '0.25rem',
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              gap: '0.5rem'
                                            }}>
                                              {teacher.subjects.map((subject, idx) => (
                                                <span 
                                                  key={idx}
                                                  style={{
                                                    padding: '0.35rem 0.75rem',
                                                    background: '#EFF6FF',
                                                    color: '#1E40AF',
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '500'
                                                  }}
                                                >
                                                  {subject}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ 
                                padding: '3rem 2rem', 
                                textAlign: 'center', 
                                color: '#6B7280', 
                                fontStyle: 'italic',
                                background: '#F9FAFB',
                              borderRadius: '12px',
                                border: '2px dashed #D1D5DB'
                              }}>
                                👨‍🏫 No teachers assigned to this class and section.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Medical Records Tab */}
                      {activeTab === 'medical' && (
                        <div>
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              Medical Records
                            </h4>
                            
                            <div style={{
                              padding: '1.5rem',
                              background: '#FFFFFF',
                              borderRadius: '12px',
                              border: '2px solid #E5E7EB',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                              marginBottom: '1.5rem',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                              }}></div>
                              
                              <div style={{ marginTop: '0.5rem' }}>
                                <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Medical Details</span>
                                <div style={{ 
                                  marginTop: '0.75rem', 
                                  padding: '1.25rem', 
                                  background: '#F9FAFB', 
                                  borderRadius: '8px',
                                  whiteSpace: 'pre-wrap',
                                  minHeight: '120px',
                                  color: '#1F2937',
                                  fontSize: '0.95rem',
                                  lineHeight: '1.6'
                                }}>
                                  {viewingStudent.medical_details || 'No medical records provided.'}
                                </div>
                              </div>
                            </div>

                            {/* Emergency Documents Card */}
                            {viewingStudent.emergency_documents_url ? (
                              <div style={{
                                padding: '1.5rem',
                                background: '#FFFFFF',
                                borderRadius: '12px',
                                border: '2px solid #F59E0B',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                position: 'relative',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '4px',
                                  background: 'linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)'
                                }}></div>
                                
                                <div style={{ marginTop: '0.5rem' }}>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Emergency Documents</span>
                                  <div style={{
                                    marginTop: '0.75rem',
                                    padding: '1rem',
                                    background: '#FEF3C7',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem'
                                  }}>
                                    <span style={{ fontSize: '1.5rem' }}>📄</span>
                                    <a 
                                      href={viewingStudent.emergency_documents_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      onClick={async (e) => {
                                        if (!viewingStudent.emergency_documents_url || viewingStudent.emergency_documents_url.includes('404')) {
                                          e.preventDefault();
                                          const newUrl = await getValidFileUrl(viewingStudent.emergency_documents_url);
                                          if (newUrl) {
                                            window.open(newUrl, '_blank');
                                          } else {
                                            alert('Unable to access emergency documents. Please try again or contact support.');
                                          }
                                        }
                                      }}
                                      style={{ 
                                        color: '#1F2937', 
                                        textDecoration: 'none',
                                        fontWeight: '500',
                                        fontSize: '1rem'
                                      }}
                                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                    >
                                      View Emergency Documents
                                    </a>
                                    <span style={{ color: '#6B7280', fontSize: '0.85rem', marginLeft: 'auto' }}>
                                      (Opens in new tab)
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div style={{
                                padding: '2rem',
                                textAlign: 'center',
                                color: '#6B7280',
                                fontStyle: 'italic',
                                background: '#F9FAFB',
                                borderRadius: '12px',
                                border: '2px dashed #D1D5DB'
                              }}>
                                📄 No emergency documents uploaded.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Academic Details Tab */}
                      {activeTab === 'academic' && (
                        <div>
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              Academic Information
                            </h4>
                            <div style={{
                              padding: '1.5rem',
                              background: '#FFFFFF',
                              borderRadius: '12px',
                              border: '2px solid #E5E7EB',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                              }}></div>
                              
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Class</span>
                                  <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                    {viewingStudent.class_name || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Section</span>
                                  <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                    {viewingStudent.section_id || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Roll Number</span>
                                  <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                    {viewingStudent.roll_number || 'N/A'}
                                  </div>
                                </div>
                                <div>
                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Status</span>
                                  <div style={{ marginTop: '0.25rem' }}>
                                    <span className={`status-badge ${viewingStudent.is_active ? 'active' : 'inactive'}`} style={{ 
                                      marginLeft: 0,
                                      padding: '0.35rem 0.75rem',
                                      borderRadius: '20px',
                              fontSize: '0.85rem',
                                      fontWeight: '600'
                            }}>
                              {viewingStudent.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                      )}

                      {/* School Transport Tab */}
                      {activeTab === 'transport' && (
                        <div>
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              School Transport Service
                            </h4>
                            
                            {viewingStudent.transport_service_enabled ? (
                              <div style={{
                                padding: '1.5rem',
                                background: '#FFFFFF',
                                borderRadius: '12px',
                                border: '2px solid #E5E7EB',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                position: 'relative',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '4px',
                                  background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                                }}></div>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                                  <div>
                                    <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Transport Service</span>
                                    <div style={{ marginTop: '0.25rem' }}>
                                      <span style={{ 
                                        color: '#059669', 
                                        fontWeight: '600',
                                        padding: '0.35rem 0.75rem',
                                        background: '#D1FAE5',
                                        borderRadius: '20px',
                                        fontSize: '0.85rem',
                                        display: 'inline-block'
                                      }}>
                                        ✓ Enabled
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {viewingStudent.pickupLocation && (
                                    <>
                                      <div>
                                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Pickup Location</span>
                                        <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                          {viewingStudent.pickupLocation.location_name || 'N/A'}
                                        </div>
                                      </div>
                                      <div>
                                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Location Address</span>
                                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                          {viewingStudent.pickupLocation.address || 'N/A'}
                                        </div>
                                      </div>
                                      <div>
                                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Route Type</span>
                                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                          {viewingStudent.transport_route_type || 'N/A'}
                                        </div>
                                      </div>
                                      {viewingStudent.transport_price && (
                                        <div>
                                          <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Transport Price</span>
                                          <div style={{ 
                                            color: '#059669', 
                                            fontWeight: '600',
                                            fontSize: '1.1rem',
                                            marginTop: '0.25rem'
                                          }}>
                                            {viewingStudent.pickupLocation?.currency || 'PKR'} {parseFloat(viewingStudent.transport_price).toLocaleString()}
              </div>
            </div>
          )}
                                      {viewingStudent.pickupLocation?.route && (
                                        <>
                                          <div>
                                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Route Name</span>
                                            <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                              {viewingStudent.pickupLocation.route.route_name || `Route #${viewingStudent.pickupLocation.route.id}`}
                                            </div>
                                          </div>
                                          {viewingStudent.pickupLocation.route.vehicle && (
                                            <>
                                              <div>
                                                <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Vehicle Number</span>
                                                <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                                  {viewingStudent.pickupLocation.route.vehicle.bus_number || 'N/A'}
                                                </div>
                                              </div>
                                              {viewingStudent.pickupLocation.route.vehicle.driver_name && (
                                                <div>
                                                  <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Driver Name</span>
                                                  <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                                                    {viewingStudent.pickupLocation.route.vehicle.driver_name}
                                                  </div>
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div style={{
                                padding: '2rem',
                                textAlign: 'center',
                                color: '#6B7280',
                                fontStyle: 'italic',
                                background: '#F9FAFB',
                                borderRadius: '12px',
                                border: '2px dashed #D1D5DB'
                              }}>
                                🚌 Transport service is disabled for this student.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Fees Payments Tab */}
                      {activeTab === 'fees' && (
                        <div>
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              Fees & Payment Information
                            </h4>
                            
                            <div style={{ 
                              padding: '1.5rem',
                              background: '#FFFFFF',
                              borderRadius: '12px',
                              border: '2px solid #E5E7EB',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                              marginBottom: '1.5rem',
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                              }}></div>
                              
                              <div style={{ marginTop: '0.5rem' }}>
                                <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Payment Method</span>
                                <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                  {viewingStudent.payment_method || 'Not specified'}
                                </div>
                              </div>
                            </div>

                            {(viewingStudent.total_monthly_fee || viewingStudent.monthly_tuition_fee || viewingStudent.transport_fee) ? (
                              <div style={{
                                padding: '1.5rem',
                                background: '#FFFFFF',
                                borderRadius: '12px',
                                border: '2px solid #E5E7EB',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                position: 'relative',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '4px',
                                  background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                                }}></div>
                                
                                <h5 style={{ color: '#1F2937', marginBottom: '1rem', fontSize: '1rem', fontWeight: '600' }}>Monthly Fee Breakdown</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.5rem' }}>
                                  {viewingStudent.monthly_tuition_fee && (
                                    <div>
                                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Monthly Tuition Fee</span>
                                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                        {viewingStudent.fee_currency || 'PKR'} {parseFloat(viewingStudent.monthly_tuition_fee).toLocaleString()}
                                      </div>
                                    </div>
                                  )}
                                  {viewingStudent.transport_fee && (
                                    <div>
                                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Transport Fee</span>
                                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                        {viewingStudent.fee_currency || 'PKR'} {parseFloat(viewingStudent.transport_fee).toLocaleString()}
                                      </div>
                                    </div>
                                  )}
                                  {viewingStudent.monthly_additional_fees && (
                                    <div>
                                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Additional Fees</span>
                                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                                        {viewingStudent.fee_currency || 'PKR'} {parseFloat(viewingStudent.monthly_additional_fees).toLocaleString()}
                                      </div>
                                    </div>
                                  )}
                                  {viewingStudent.total_monthly_fee && (
                                    <div>
                                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Total Monthly Fee</span>
                                      <div style={{ 
                                        color: '#059669', 
                                        fontSize: '1.2rem', 
                                        fontWeight: '700', 
                                        marginTop: '0.25rem' 
                                      }}>
                                        {viewingStudent.fee_currency || 'PKR'} {parseFloat(viewingStudent.total_monthly_fee).toLocaleString()}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div style={{
                                padding: '2rem',
                                textAlign: 'center',
                                color: '#6B7280',
                                fontStyle: 'italic',
                                background: '#F9FAFB',
                                borderRadius: '12px',
                                border: '2px dashed #D1D5DB'
                              }}>
                                💰 No fee information available.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Documents Tab */}
                      {activeTab === 'documents' && (
                        <div>
                          <div className="detail-section" style={{ marginBottom: '2rem' }}>
                            <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                              Student Documents
                            </h4>
                            <div>
                              {viewingStudent.documents && viewingStudent.documents.filter(doc => !doc.parent_id).length > 0 ? (
                                <div style={{ display: 'grid', gap: '1rem' }}>
                                  {viewingStudent.documents.filter(doc => !doc.parent_id).map((doc, index) => (
                                    <div 
                                      key={doc.id || index} 
                                      style={{
                                        padding: '1.25rem',
                                        background: '#FFFFFF',
                                        borderRadius: '12px',
                                        border: '2px solid #E5E7EB',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        overflow: 'hidden'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#FF8C42';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 140, 66, 0.15)';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#E5E7EB';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                                      }}></div>
                                      
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, marginTop: '0.5rem' }}>
                                        <div style={{
                                          width: '48px',
                                          height: '48px',
                                          borderRadius: '8px',
                                          background: '#EFF6FF',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '1.75rem'
                                        }}>
                                          {doc.document_type?.toLowerCase().includes('birth') ? '🎂' :
                                           doc.document_type?.toLowerCase().includes('certificate') ? '📜' :
                                           doc.document_type?.toLowerCase().includes('id') ? '🆔' :
                                           doc.document_type?.toLowerCase().includes('medical') ? '🏥' :
                                           '📄'}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                          <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '1rem', marginBottom: '0.35rem' }}>
                                            {doc.document_type || 'Document'}
                                          </div>
                                          <a 
                                            href={doc.document_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            onClick={async (e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              console.log('Document URL before processing:', doc.document_url);
                                              const newUrl = await getValidFileUrl(doc.document_url);
                                              console.log('Document URL after processing:', newUrl);
                                              if (newUrl) {
                                                window.open(newUrl, '_blank');
                                              } else {
                                                alert('Unable to access document. Please try again or contact support.');
                                              }
                                            }}
                                            style={{ 
                                              color: '#1F2937', 
                                              textDecoration: 'none',
                                              fontSize: '0.9rem',
                                              fontWeight: '500'
                                            }}
                                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                          >
                                            {doc.file_name || 'View Document'} →
                                          </a>
                                        </div>
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem', marginTop: '0.5rem' }}>
                                        {doc.file_size && (
                                          <span style={{ 
                                            color: '#6B7280', 
                                            fontSize: '0.85rem',
                                            padding: '0.25rem 0.5rem',
                                            background: '#F3F4F6',
                                            borderRadius: '6px'
                                          }}>
                                            {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                                          </span>
                                        )}
                                        {doc.uploaded_at && (
                                          <span style={{ 
                                            color: '#9CA3AF', 
                                            fontSize: '0.75rem'
                                          }}>
                                            {new Date(doc.uploaded_at).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ 
                                  padding: '3rem 2rem', 
                                  textAlign: 'center',
                                  color: '#6B7280', 
                                  fontStyle: 'italic',
                                  background: '#F9FAFB',
                                  borderRadius: '12px',
                                  border: '2px dashed #D1D5DB'
                                }}>
                                  📄 No documents uploaded yet.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Edit Student Popup */}
          {editingStudent && (
            <div className="popup-overlay" onClick={closeEditPopup}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
                  <h3 style={{ color: '#FFFFFF', margin: 0 }}>Edit Student</h3>
                  <button className="close-btn" onClick={closeEditPopup} style={{ color: '#FFFFFF', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    ×
                  </button>
                </div>
                
                <form onSubmit={handleEditSubmit} className="edit-student-form" style={{ padding: '2rem' }}>
                  <div className="form-section" style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#FF8C42', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>A) Student Information</h4>
                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label htmlFor="editFirstName">First Name *</label>
                        <input
                          type="text"
                          id="editFirstName"
                          value={editForm.firstName}
                          onChange={(e) => handleEditInputChange('firstName', e.target.value)}
                          className={editErrors.firstName ? 'error' : ''}
                          placeholder="Enter first name"
                          style={{ width: '100%', padding: '0.75rem', border: editErrors.firstName ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {editErrors.firstName && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.firstName}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="editMiddleName">Middle Name</label>
                        <input
                          type="text"
                          id="editMiddleName"
                          value={editForm.middleName}
                          onChange={(e) => handleEditInputChange('middleName', e.target.value)}
                          placeholder="Enter middle name (optional)"
                          style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="editLastName">Last Name *</label>
                        <input
                          type="text"
                          id="editLastName"
                          value={editForm.lastName}
                          onChange={(e) => handleEditInputChange('lastName', e.target.value)}
                          className={editErrors.lastName ? 'error' : ''}
                          placeholder="Enter last name"
                          style={{ width: '100%', padding: '0.75rem', border: editErrors.lastName ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {editErrors.lastName && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.lastName}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label htmlFor="editEmail">Email *</label>
                        <input
                          type="email"
                          id="editEmail"
                          value={editForm.email}
                          onChange={(e) => handleEditInputChange('email', e.target.value)}
                          className={editErrors.email ? 'error' : ''}
                          placeholder="Enter email"
                          style={{ width: '100%', padding: '0.75rem', border: editErrors.email ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {editErrors.email && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.email}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="editPhone">Student Phone *</label>
                        <div className="phone-input-wrapper" style={{ display: 'flex', gap: '8px' }}>
                          <select
                            className="country-code-select"
                            value={receptionistEditStudentPhoneCountryCode}
                            onChange={(e) => setReceptionistEditStudentPhoneCountryCode(e.target.value)}
                            style={{ flexShrink: 0, minWidth: '140px', maxWidth: '180px', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          >
                            <option value="+1">🇺🇸 +1 (US/CA)</option>
                            <option value="+44">🇬🇧 +44 (UK)</option>
                            <option value="+91">🇮🇳 +91 (India)</option>
                            <option value="+92">🇵🇰 +92 (Pakistan)</option>
                            <option value="+971">🇦🇪 +971 (UAE)</option>
                            <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                            <option value="+974">🇶🇦 +974 (Qatar)</option>
                            <option value="+965">🇰🇼 +965 (Kuwait)</option>
                            <option value="+973">🇧🇭 +973 (Bahrain)</option>
                            <option value="+968">🇴🇲 +968 (Oman)</option>
                            <option value="+961">🇱🇧 +961 (Lebanon)</option>
                            <option value="+962">🇯🇴 +962 (Jordan)</option>
                            <option value="+20">🇪🇬 +20 (Egypt)</option>
                            <option value="+27">🇿🇦 +27 (South Africa)</option>
                            <option value="+61">🇦🇺 +61 (Australia)</option>
                            <option value="+64">🇳🇿 +64 (New Zealand)</option>
                            <option value="+86">🇨🇳 +86 (China)</option>
                            <option value="+81">🇯🇵 +81 (Japan)</option>
                            <option value="+82">🇰🇷 +82 (South Korea)</option>
                            <option value="+65">🇸🇬 +65 (Singapore)</option>
                            <option value="+60">🇲🇾 +60 (Malaysia)</option>
                            <option value="+66">🇹🇭 +66 (Thailand)</option>
                            <option value="+62">🇮🇩 +62 (Indonesia)</option>
                            <option value="+63">🇵🇭 +63 (Philippines)</option>
                            <option value="+84">🇻🇳 +84 (Vietnam)</option>
                            <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                            <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                            <option value="+977">🇳🇵 +977 (Nepal)</option>
                            <option value="+33">🇫🇷 +33 (France)</option>
                            <option value="+49">🇩🇪 +49 (Germany)</option>
                            <option value="+39">🇮🇹 +39 (Italy)</option>
                            <option value="+34">🇪🇸 +34 (Spain)</option>
                            <option value="+31">🇳🇱 +31 (Netherlands)</option>
                            <option value="+32">🇧🇪 +32 (Belgium)</option>
                            <option value="+41">🇨🇭 +41 (Switzerland)</option>
                            <option value="+43">🇦🇹 +43 (Austria)</option>
                            <option value="+46">🇸🇪 +46 (Sweden)</option>
                            <option value="+47">🇳🇴 +47 (Norway)</option>
                            <option value="+45">🇩🇰 +45 (Denmark)</option>
                            <option value="+358">🇫🇮 +358 (Finland)</option>
                            <option value="+7">🇷🇺 +7 (Russia)</option>
                            <option value="+90">🇹🇷 +90 (Turkey)</option>
                            <option value="+52">🇲🇽 +52 (Mexico)</option>
                            <option value="+55">🇧🇷 +55 (Brazil)</option>
                            <option value="+54">🇦🇷 +54 (Argentina)</option>
                            <option value="+57">🇨🇴 +57 (Colombia)</option>
                            <option value="+234">🇳🇬 +234 (Nigeria)</option>
                            <option value="+254">🇰🇪 +254 (Kenya)</option>
                            <option value="+212">🇲🇦 +212 (Morocco)</option>
                            <option value="+972">🇮🇱 +972 (Israel)</option>
                            <option value="+98">🇮🇷 +98 (Iran)</option>
                            <option value="+964">🇮🇶 +964 (Iraq)</option>
                            <option value="+967">🇾🇪 +967 (Yemen)</option>
                            <option value="+963">🇸🇾 +963 (Syria)</option>
                          </select>
                          <input
                            type="tel"
                            id="editPhone"
                            value={editForm.phone}
                            onChange={(e) => handleEditInputChange('phone', e.target.value)}
                            className={editErrors.phone ? 'error' : ''}
                            placeholder="e.g. 300 1234567"
                            style={{ flex: 1, padding: '0.75rem', border: editErrors.phone ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        {editErrors.phone && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.phone}</span>
                        )}
                      </div>
                    </div>

                    
                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label htmlFor="editDateOfBirth">Date of Birth *</label>
                        <input
                          type="date"
                          id="editDateOfBirth"
                          value={editForm.dateOfBirth}
                          onChange={(e) => handleEditInputChange('dateOfBirth', e.target.value)}
                          className={editErrors.dateOfBirth ? 'error' : ''}
                          style={{ width: '100%', padding: '0.75rem', border: editErrors.dateOfBirth ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {editErrors.dateOfBirth && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.dateOfBirth}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="editGender">Gender *</label>
                        <select
                          id="editGender"
                          value={editForm.gender}
                          onChange={(e) => handleEditInputChange('gender', e.target.value)}
                          className={editErrors.gender ? 'error' : ''}
                          style={{ width: '100%', padding: '0.75rem', border: editErrors.gender ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                        {editErrors.gender && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.gender}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="editNationality">Nationality *</label>
                        <input
                          type="text"
                          id="editNationality"
                          value={editForm.nationality}
                          onChange={(e) => handleEditInputChange('nationality', e.target.value)}
                          className={editErrors.nationality ? 'error' : ''}
                          placeholder="Enter nationality"
                          style={{ width: '100%', padding: '0.75rem', border: editErrors.nationality ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {editErrors.nationality && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.nationality}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label htmlFor="editAdmissionDate">Admission Date *</label>
                        <input
                          type="date"
                          id="editAdmissionDate"
                          value={editForm.admissionDate}
                          onChange={(e) => handleEditInputChange('admissionDate', e.target.value)}
                          className={editErrors.admissionDate ? 'error' : ''}
                          style={{ width: '100%', padding: '0.75rem', border: editErrors.admissionDate ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {editErrors.admissionDate && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.admissionDate}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="editAddress">Address *</label>
                        <input
                          type="text"
                          id="editAddress"
                          value={editForm.address}
                          onChange={(e) => handleEditInputChange('address', e.target.value)}
                          className={editErrors.address ? 'error' : ''}
                          placeholder="Enter address"
                          style={{ width: '100%', padding: '0.75rem', border: editErrors.address ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {editErrors.address && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.address}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label htmlFor="editPreviousSchool">Previous School (optional)</label>
                        <input
                          type="text"
                          id="editPreviousSchool"
                          value={editForm.previousSchool}
                          onChange={(e) => handleEditInputChange('previousSchool', e.target.value)}
                          placeholder="Enter previous school (if applicable)"
                          style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="editPreviousSchoolGrade">Grade/Percentage from Previous School (optional)</label>
                        <input
                          type="text"
                          id="editPreviousSchoolGrade"
                          value={editForm.previousSchoolGrade}
                          onChange={(e) => handleEditInputChange('previousSchoolGrade', e.target.value)}
                          placeholder="e.g., A+, 95%, Grade 8, etc."
                          style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                      </div>
                    </div>

                    <div className="form-row" style={{ marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label htmlFor="editStudentIdNo">Student ID No. (assigned later)</label>
                        <input
                          type="text"
                          id="editStudentIdNo"
                          value={editForm.studentIdNo}
                          onChange={(e) => handleEditInputChange('studentIdNo', e.target.value)}
                          placeholder="Leave blank; will be assigned after admission"
                          style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                      </div>
                    </div>

                    <div className="form-row" style={{ marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label htmlFor="editProfilePicture">Profile Picture</label>
                        <input
                          type="file"
                          id="editProfilePicture"
                          name="editProfilePicture"
                          onChange={handleEditFileChange}
                          accept="image/*"
                          style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {editForm.profilePicture && (
                          <span style={{ fontSize: '0.85rem', color: '#059669', marginTop: '0.5rem', display: 'block' }}>
                            Selected: {editForm.profilePicture.name}
                          </span>
                        )}
                        {editingStudent?.profile_picture_url && (
                          <div style={{ marginTop: '1rem' }}>
                            <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6B7280' }}>Current picture:</p>
                            <img 
                              src={editingStudent.profile_picture_url} 
                              alt="Current profile" 
                              style={{ 
                                width: '100px', 
                                height: '100px', 
                                objectFit: 'cover', 
                                borderRadius: '50%',
                                border: '3px solid #FF8C42'
                              }}
                              onError={async (e) => {
                                const newUrl = await getValidFileUrl(editingStudent.profile_picture_url);
                                if (newUrl && newUrl !== editingStudent.profile_picture_url) {
                                  e.target.src = newUrl;
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-section" style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <h4 style={{ color: '#FF8C42', marginBottom: 0, fontSize: '1.1rem', fontWeight: '600' }}>B) Parent/Guardian Information</h4>
                      <button
                        type="button"
                        onClick={addEditParent}
                        style={{
                          padding: '0.4rem 0.8rem',
                          backgroundColor: '#FF8C42',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '0.85rem',
                          width: 'fit-content',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        + Add Another Parent
                      </button>
                    </div>
                    
                    {editForm.parents && Array.isArray(editForm.parents) && editForm.parents.length > 0 ? (
                      editForm.parents.map((parent, parentIndex) => {
                        return (
                      <div key={parentIndex} style={{ 
                        marginBottom: '2rem', 
                        padding: '1.5rem', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '12px',
                        backgroundColor: '#FAFAFA',
                        position: 'relative'
                      }}>
                        {editForm.parents.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEditParent(parentIndex)}
                            style={{
                              position: 'absolute',
                              top: '1rem',
                              right: '1rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              width: 'fit-content',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Remove Parent
                          </button>
                        )}
                        
                        <h4 style={{ marginBottom: '1rem', color: '#1F2937' }}>
                          Parent {parentIndex + 1}
                        </h4>
                        
                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                          <div className="form-group">
                            <label htmlFor={`editParentName-${parentIndex}`}>Parent Name *</label>
                            <input
                              type="text"
                              id={`editParentName-${parentIndex}`}
                              value={parent.name}
                              onChange={(e) => handleEditParentChange(parentIndex, 'name', e.target.value)}
                              className={editErrors[`parents.${parentIndex}.name`] ? 'error' : ''}
                              placeholder="Enter parent name"
                              style={{ width: '100%', padding: '0.75rem', border: editErrors[`parents.${parentIndex}.name`] ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                            />
                            {editErrors[`parents.${parentIndex}.name`] && (
                              <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors[`parents.${parentIndex}.name`]}</span>
                            )}
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor={`editParentEmail-${parentIndex}`}>Parent Email *</label>
                            <input
                              type="email"
                              id={`editParentEmail-${parentIndex}`}
                              value={parent.email}
                              onChange={(e) => handleEditParentChange(parentIndex, 'email', e.target.value)}
                              className={editErrors[`parents.${parentIndex}.email`] ? 'error' : ''}
                              placeholder="Enter parent email"
                              style={{ width: '100%', padding: '0.75rem', border: editErrors[`parents.${parentIndex}.email`] ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                            />
                            {editErrors[`parents.${parentIndex}.email`] && (
                              <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors[`parents.${parentIndex}.email`]}</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                          <div className="form-group">
                            <label htmlFor={`editParentPhone-${parentIndex}`}>Parent Phone *</label>
                            <div className="phone-input-wrapper" style={{ display: 'flex', gap: '8px' }}>
                              <select
                                className="country-code-select"
                                value={(receptionistEditParentPhoneCountryCodes && receptionistEditParentPhoneCountryCodes[parentIndex]) || '+92'}
                                onChange={(e) => {
                                  const currentCodes = receptionistEditParentPhoneCountryCodes || [];
                                  const newCodes = [...currentCodes];
                                  while (newCodes.length <= parentIndex) {
                                    newCodes.push('+92');
                                  }
                                  newCodes[parentIndex] = e.target.value;
                                  setReceptionistEditParentPhoneCountryCodes(newCodes);
                                }}
                                style={{ flexShrink: 0, minWidth: '140px', maxWidth: '180px', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                              >
                                <option value="+92">🇵🇰 +92 (Pakistan)</option>
                                <option value="+1">🇺🇸 +1 (US/CA)</option>
                                <option value="+44">🇬🇧 +44 (UK)</option>
                                <option value="+91">🇮🇳 +91 (India)</option>
                                <option value="+971">🇦🇪 +971 (UAE)</option>
                                <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                              </select>
                              <input
                                type="tel"
                                id={`editParentPhone-${parentIndex}`}
                                value={parent.phone}
                                onChange={(e) => handleEditParentChange(parentIndex, 'phone', e.target.value)}
                                className={editErrors[`parents.${parentIndex}.phone`] ? 'error' : ''}
                                placeholder="e.g. 300 1234567"
                                style={{ flex: 1, padding: '0.75rem', border: editErrors[`parents.${parentIndex}.phone`] ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '6px' }}
                              />
                            </div>
                            {editErrors[`parents.${parentIndex}.phone`] && (
                              <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors[`parents.${parentIndex}.phone`]}</span>
                            )}
                          </div>

                          <div className="form-group">
                            <label>Relationship *</label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                type="button"
                                onClick={() => handleEditParentChange(parentIndex, 'relationship', 'Mother')}
                                style={{
                                  flex: 1,
                                  padding: '0.75rem',
                                  border: parent.relationship === 'Mother' ? '2px solid #FF8C42' : '1px solid #D1D5DB',
                                  borderRadius: '6px',
                                  background: parent.relationship === 'Mother' ? '#FF8C42' : '#FFFFFF',
                                  color: parent.relationship === 'Mother' ? '#FFFFFF' : '#1F2937',
                                  fontWeight: parent.relationship === 'Mother' ? '600' : '400',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Mother
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditParentChange(parentIndex, 'relationship', 'Father')}
                                style={{
                                  flex: 1,
                                  padding: '0.75rem',
                                  border: parent.relationship === 'Father' ? '2px solid #FF8C42' : '1px solid #D1D5DB',
                                  borderRadius: '6px',
                                  background: parent.relationship === 'Father' ? '#FF8C42' : '#FFFFFF',
                                  color: parent.relationship === 'Father' ? '#FFFFFF' : '#1F2937',
                                  fontWeight: parent.relationship === 'Father' ? '600' : '400',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Father
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditParentChange(parentIndex, 'relationship', 'Guardian')}
                                style={{
                                  flex: 1,
                                  padding: '0.75rem',
                                  border: parent.relationship === 'Guardian' ? '2px solid #FF8C42' : '1px solid #D1D5DB',
                                  borderRadius: '6px',
                                  background: parent.relationship === 'Guardian' ? '#FF8C42' : '#FFFFFF',
                                  color: parent.relationship === 'Guardian' ? '#FFFFFF' : '#1F2937',
                                  fontWeight: parent.relationship === 'Guardian' ? '600' : '400',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Guardian
                              </button>
                            </div>
                            {editErrors[`parents.${parentIndex}.relationship`] && (
                              <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors[`parents.${parentIndex}.relationship`]}</span>
                            )}
                          </div>
                        </div>

                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                          <div className="form-group">
                            <label htmlFor={`editParentPrimaryContact-${parentIndex}`}>Contact Priority</label>
                            <select
                              id={`editParentPrimaryContact-${parentIndex}`}
                              value={parent.primaryContact}
                              onChange={(e) => handleEditParentChange(parentIndex, 'primaryContact', e.target.value)}
                              style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                            >
                              <option value="primary">Primary</option>
                              <option value="secondary">Secondary</option>
                            </select>
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor={`editParentAddress-${parentIndex}`}>Parent Address (optional)</label>
                            <input
                              type="text"
                              id={`editParentAddress-${parentIndex}`}
                              value={parent.address}
                              onChange={(e) => handleEditParentChange(parentIndex, 'address', e.target.value)}
                              placeholder="If different from student's address"
                              style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                            />
                          </div>
                        </div>

                        <div className="form-row" style={{ marginBottom: '1rem' }}>
                          <div className="form-group" style={{ width: '100%' }}>
                            <label style={{ marginBottom: '0.75rem', display: 'block', fontWeight: '500' }}>
                              Permissions
                            </label>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                              gap: '0.75rem',
                              padding: '1rem',
                              background: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              borderRadius: '12px',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                            }}>
                              {[
                                { field: 'canPickup', label: 'Can pick from school' },
                                { field: 'isEmergencyContact', label: 'Emergency contact' },
                                { field: 'paysFees', label: 'Pays fees' }
                              ].map((permission) => {
                                const isChecked = parent[permission.field];
                                return (
                                  <div
                                    key={permission.field}
                                    onClick={() => {
                                      handleEditParentChange(parentIndex, permission.field, !isChecked);
                                    }}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '1rem',
                                      cursor: 'pointer',
                                      borderRadius: '10px',
                                      background: isChecked ? '#FF8C42' : '#FFFFFF',
                                      border: isChecked ? '2px solid #E67E22' : '1px solid #E5E7EB',
                                      transition: 'all 0.2s ease',
                                      position: 'relative'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isChecked) {
                                        e.currentTarget.style.background = '#FFF4ED';
                                        e.currentTarget.style.borderColor = '#FFD4B3';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isChecked) {
                                        e.currentTarget.style.background = '#FFFFFF';
                                        e.currentTarget.style.borderColor = '#E5E7EB';
                                      }
                                    }}
                                  >
                                    <div style={{
                                      width: '24px',
                                      height: '24px',
                                      minWidth: '24px',
                                      marginRight: '0.75rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '6px',
                                      border: isChecked ? '2px solid #FFFFFF' : '2px solid #D1D5DB',
                                      background: isChecked ? '#FFFFFF' : '#FFFFFF',
                                      transition: 'all 0.2s ease'
                                    }}>
                                      {isChecked && (
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="#FF8C42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      )}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ 
                                        fontWeight: '500',
                                        fontSize: '0.95rem',
                                        color: isChecked ? '#FFFFFF' : '#1F2937'
                                      }}>
                                        {permission.label}
                                      </div>
                                    </div>
                                    {isChecked && (
                                      <div style={{
                                        position: 'absolute',
                                        top: '0.5rem',
                                        right: '0.5rem',
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        background: '#FFFFFF'
                                      }} />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        
                        {/* Parent Documents Section */}
                        <div className="form-row" style={{ gridColumn: '1 / -1', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #E5E7EB' }}>
                          <div className="form-group" style={{ width: '100%' }}>
                            <label style={{ marginBottom: '0.75rem', display: 'block', fontWeight: '500', color: '#1F2937' }}>
                              Parent Documents
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              {/* Parent National ID */}
                              <div>
                                <label htmlFor={`editParentNationalId-${parentIndex}`} style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                                  Parent National ID
                                </label>
                                <input
                                  type="file"
                                  id={`editParentNationalId-${parentIndex}`}
                                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      handleEditParentDocumentAdd(parentIndex, 'Parent National ID', file);
                                      e.target.value = '';
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '6px',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer'
                                  }}
                                />
                                {parent.documents && parent.documents.filter(doc => doc.document_type === 'Parent National ID' || doc.type === 'Parent National ID').length > 0 && (
                                  <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', background: '#F3F4F6', borderRadius: '6px' }}>
                                    <span style={{ fontSize: '1.2rem' }}>🆔</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1F2937' }}>
                                        Parent National ID
                                      </div>
                                      <div style={{ fontSize: '0.75rem', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {parent.documents.find(doc => doc.document_type === 'Parent National ID' || doc.type === 'Parent National ID')?.file_name || parent.documents.find(doc => doc.document_type === 'Parent National ID' || doc.type === 'Parent National ID')?.fileName || 'Document'}
                                      </div>
                                    </div>
                                    {parent.documents.find(doc => doc.document_type === 'Parent National ID' || doc.type === 'Parent National ID')?.document_url && (
                                      <a
                                        href={parent.documents.find(doc => doc.document_type === 'Parent National ID' || doc.type === 'Parent National ID')?.document_url || parent.documents.find(doc => doc.document_type === 'Parent National ID' || doc.type === 'Parent National ID')?.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          padding: '0.25rem 0.5rem',
                                          background: '#FF8C42',
                                          color: 'white',
                                          borderRadius: '4px',
                                          fontSize: '0.75rem',
                                          fontWeight: '500',
                                          textDecoration: 'none'
                                        }}
                                      >
                                        View
                                      </a>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => handleEditParentDocumentRemove(parentIndex, 'Parent National ID')}
                                      style={{
                                        padding: '0.25rem 0.5rem',
                                        background: '#EF4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                              
                              {/* Parent Passport */}
                              <div>
                                <label htmlFor={`editParentPassport-${parentIndex}`} style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                                  Parent Passport
                                </label>
                                <input
                                  type="file"
                                  id={`editParentPassport-${parentIndex}`}
                                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      handleEditParentDocumentAdd(parentIndex, 'Parent Passport', file);
                                      e.target.value = '';
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '6px',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer'
                                  }}
                                />
                                {parent.documents && parent.documents.filter(doc => doc.document_type === 'Parent Passport' || doc.type === 'Parent Passport').length > 0 && (
                                  <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', background: '#F3F4F6', borderRadius: '6px' }}>
                                    <span style={{ fontSize: '1.2rem' }}>📘</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1F2937' }}>
                                        Parent Passport
                                      </div>
                                      <div style={{ fontSize: '0.75rem', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {parent.documents.find(doc => doc.document_type === 'Parent Passport' || doc.type === 'Parent Passport')?.file_name || parent.documents.find(doc => doc.document_type === 'Parent Passport' || doc.type === 'Parent Passport')?.fileName || 'Document'}
                                      </div>
                                    </div>
                                    {parent.documents.find(doc => doc.document_type === 'Parent Passport' || doc.type === 'Parent Passport')?.document_url && (
                                      <a
                                        href={parent.documents.find(doc => doc.document_type === 'Parent Passport' || doc.type === 'Parent Passport')?.document_url || parent.documents.find(doc => doc.document_type === 'Parent Passport' || doc.type === 'Parent Passport')?.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          padding: '0.25rem 0.5rem',
                                          background: '#FF8C42',
                                          color: 'white',
                                          borderRadius: '4px',
                                          fontSize: '0.75rem',
                                          fontWeight: '500',
                                          textDecoration: 'none'
                                        }}
                                      >
                                        View
                                      </a>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => handleEditParentDocumentRemove(parentIndex, 'Parent Passport')}
                                      style={{
                                        padding: '0.25rem 0.5rem',
                                        background: '#EF4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Emergency Contact Fields in Parent Section */}
                        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginTop: '1rem', marginBottom: '1rem', paddingTop: '1rem', borderTop: '1px solid #E5E7EB' }}>
                          <div className="form-group">
                            <label htmlFor={`editParentEmergencyContact-${parentIndex}`}>Emergency Contact *</label>
                            <input
                              type="text"
                              id={`editParentEmergencyContact-${parentIndex}`}
                              value={parentIndex === 0 ? editForm.emergencyContact : ''}
                              onChange={(e) => {
                                if (parentIndex === 0) {
                                  handleEditInputChange('emergencyContact', e.target.value);
                                }
                              }}
                              disabled={parentIndex !== 0}
                              className={editErrors.emergencyContact ? 'error' : ''}
                              placeholder="Enter emergency contact"
                              style={{ 
                                width: '100%', 
                                padding: '0.75rem', 
                                border: editErrors.emergencyContact ? '2px solid #EF4444' : '1px solid #D1D5DB', 
                                borderRadius: '6px',
                                backgroundColor: parentIndex !== 0 ? '#F3F4F6' : '#FFFFFF',
                                cursor: parentIndex !== 0 ? 'not-allowed' : 'text'
                              }}
                            />
                            {parentIndex === 0 && editErrors.emergencyContact && (
                              <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.emergencyContact}</span>
                            )}
                            {parentIndex !== 0 && (
                              <small style={{ color: '#6B7280', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
                                Emergency contact is set from the first parent
                              </small>
                            )}
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor={`editParentEmergencyPhone-${parentIndex}`}>Emergency Phone *</label>
                            <div className="phone-input-wrapper" style={{ display: 'flex', gap: '8px' }}>
                              <select
                                className="country-code-select"
                                value={(receptionistEditEmergencyPhoneCountryCode && receptionistEditEmergencyPhoneCountryCode) || '+92'}
                                onChange={(e) => setReceptionistEditEmergencyPhoneCountryCode(e.target.value)}
                                disabled={parentIndex !== 0}
                                style={{ 
                                  flexShrink: 0, 
                                  minWidth: '140px', 
                                  maxWidth: '180px', 
                                  padding: '0.75rem', 
                                  border: '1px solid #D1D5DB', 
                                  borderRadius: '6px',
                                  backgroundColor: parentIndex !== 0 ? '#F3F4F6' : '#FFFFFF',
                                  cursor: parentIndex !== 0 ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <option value="+92">🇵🇰 +92 (Pakistan)</option>
                                <option value="+1">🇺🇸 +1 (US/CA)</option>
                                <option value="+44">🇬🇧 +44 (UK)</option>
                                <option value="+91">🇮🇳 +91 (India)</option>
                                <option value="+971">🇦🇪 +971 (UAE)</option>
                                <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                              </select>
                              <input
                                type="tel"
                                id={`editParentEmergencyPhone-${parentIndex}`}
                                value={parentIndex === 0 ? editForm.emergencyPhone : ''}
                                onChange={(e) => {
                                  if (parentIndex === 0) {
                                    handleEditInputChange('emergencyPhone', e.target.value);
                                  }
                                }}
                                disabled={parentIndex !== 0}
                                className={editErrors.emergencyPhone ? 'error' : ''}
                                placeholder="Enter emergency phone"
                                style={{ 
                                  flex: 1, 
                                  padding: '0.75rem', 
                                  border: editErrors.emergencyPhone ? '2px solid #EF4444' : '1px solid #D1D5DB', 
                                  borderRadius: '6px',
                                  backgroundColor: parentIndex !== 0 ? '#F3F4F6' : '#FFFFFF',
                                  cursor: parentIndex !== 0 ? 'not-allowed' : 'text'
                                }}
                              />
                            </div>
                            {parentIndex === 0 && editErrors.emergencyPhone && (
                              <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem' }}>{editErrors.emergencyPhone}</span>
                            )}
                            {parentIndex !== 0 && (
                              <small style={{ color: '#6B7280', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
                                Emergency phone is set from the first parent
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                        );
                      })
                    ) : (
                      <div style={{
                        padding: '1.5rem',
                        background: '#FFFFFF',
                        borderRadius: '12px',
                        border: '2px solid #E5E7EB',
                        textAlign: 'center',
                        color: '#6B7280',
                        marginBottom: '2rem'
                      }}>
                        No parent information available. Please add a parent.
                      </div>
                    )}
                  </div>

                  <div className="form-section" style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#FF8C42', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>C) SIBLINGS ATTENDING THE SAME SCHOOL</h4>
                    <div style={{ padding: '1rem', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      {loadingEditSiblings ? (
                        <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Loading siblings...</p>
                      ) : editSiblings.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
                          {editSiblings.map((sibling) => (
                            <div key={sibling.id} style={{ padding: '1rem', background: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                              <div style={{ marginBottom: '0.75rem' }}>
                                <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1F2937' }}>
                                  {sibling.first_name} {sibling.middle_name ? sibling.middle_name + ' ' : ''}{sibling.last_name}
                                </h5>
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#6B7280', lineHeight: '1.6' }}>
                                <p style={{ margin: '0.25rem 0' }}><strong>Email:</strong> {sibling.email || 'N/A'}</p>
                                <p style={{ margin: '0.25rem 0' }}><strong>Class:</strong> {sibling.classes?.class_name || 'N/A'}</p>
                                <p style={{ margin: '0.25rem 0' }}><strong>Section:</strong> {sibling.section_id || 'N/A'}</p>
                                <p style={{ margin: '0.25rem 0' }}><strong>Date of Birth:</strong> {sibling.date_of_birth ? new Date(sibling.date_of_birth).toLocaleDateString() : 'N/A'}</p>
                                <p style={{ margin: '0.25rem 0' }}><strong>Gender:</strong> {sibling.gender || 'N/A'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>No siblings found</p>
                      )}
                    </div>
                  </div>

                  <div className="form-section" style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#FF8C42', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>D) TEACHER DETAILS</h4>
                    <div style={{ padding: '1rem', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      {!editForm.classId || !editForm.sectionId ? (
                        <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Please select a class and section to view assigned teachers.</p>
                      ) : loadingEditTeachers ? (
                        <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Loading teachers...</p>
                      ) : editTeachers.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
                          {editTeachers.map((teacher) => (
                            <div key={teacher.id} style={{ padding: '1rem', background: '#FFFFFF', borderRadius: '8px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                              <div style={{ marginBottom: '0.75rem' }}>
                                <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1F2937' }}>
                                  {teacher.first_name} {teacher.last_name}
                                </h5>
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#6B7280', lineHeight: '1.6' }}>
                                <p style={{ margin: '0.25rem 0' }}><strong>Email:</strong> {teacher.email || 'N/A'}</p>
                                <p style={{ margin: '0.25rem 0' }}><strong>Phone:</strong> {teacher.phone || 'N/A'}</p>
                                <p style={{ margin: '0.25rem 0' }}><strong>Qualification:</strong> {teacher.qualification || 'N/A'}</p>
                                {teacher.subjects && Array.isArray(teacher.subjects) && teacher.subjects.length > 0 && (
                                  <p style={{ margin: '0.25rem 0' }}><strong>Subjects:</strong> {teacher.subjects.join(', ')}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>No teachers assigned to this class and section.</p>
                      )}
                    </div>
                  </div>

                  <div className="form-section" style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#FF8C42', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>E) Medical Records</h4>
                    <div className="form-row">
                      <div className="form-group" style={{ width: '100%' }}>
                        <label htmlFor="editMedicalDetails">Medical Details</label>
                        <textarea
                          id="editMedicalDetails"
                          value={editForm.medicalDetails}
                          onChange={(e) => handleEditInputChange('medicalDetails', e.target.value)}
                          placeholder="Enter medical records here..."
                          rows="6"
                          style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px', resize: 'vertical' }}
                        />
                      </div>
                    </div>
                    
                    <div className="form-row" style={{ marginTop: '1rem' }}>
                      <div className="form-group" style={{ width: '100%' }}>
                        <label htmlFor="editEmergencyDocuments">Emergency Documents (PDF, DOC, DOCX, JPG, PNG)</label>
                        <input
                          type="file"
                          id="editEmergencyDocuments"
                          onChange={handleEditEmergencyDocumentsChange}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {editForm.emergencyDocuments && (
                          <span style={{ fontSize: '0.85rem', color: '#059669', marginTop: '0.5rem', display: 'block' }}>
                            Selected: {editForm.emergencyDocuments.name}
                          </span>
                        )}
                        {editForm.emergencyDocumentsUrl && !editForm.emergencyDocuments && (
                          <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#FEF3C7', borderRadius: '6px', border: '1px solid #FDE68A' }}>
                            <span style={{ fontSize: '0.9rem', color: '#92400E', marginRight: '0.5rem' }}>Current document: </span>
                            <a 
                              href={editForm.emergencyDocumentsUrl} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              onClick={async (e) => {
                                e.preventDefault();
                                const newUrl = await getValidFileUrl(editForm.emergencyDocumentsUrl);
                                if (newUrl) {
                                  window.open(newUrl, '_blank');
                                } else {
                                  alert('Unable to access emergency documents. Please try again.');
                                }
                              }}
                              style={{ 
                                color: '#D97706', 
                                textDecoration: 'none',
                                fontWeight: '500',
                                fontSize: '0.9rem'
                              }}
                              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                            >
                              View Emergency Documents →
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-section" style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#FF8C42', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>F) ACADEMIC HISTORY</h4>
                    <div style={{ padding: '1rem', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #E5E7EB' }}>
                        <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>1. Previous school(s) attended:</p>
                        <p style={{ margin: 0, fontSize: '0.95rem', color: '#1F2937' }}>
                          {editForm.previousSchool ? editForm.previousSchool : 'Not specified'}
                        </p>
                      </div>
                      {editForm.previousSchoolGrade && (
                        <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #E5E7EB' }}>
                          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>Grade/Percentage from Previous School:</p>
                          <p style={{ margin: 0, fontSize: '0.95rem', color: '#1F2937' }}>
                            {editForm.previousSchoolGrade}
                          </p>
                        </div>
                      )}
                      <div>
                        <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>2. Grade/Class Assigned:</p>
                        <p style={{ margin: 0, fontSize: '0.95rem', color: '#1F2937' }}>
                          {editForm.classId ? (
                            <>
                              {classes.find(c => c.id === parseInt(editForm.classId))?.class_name || 'Class ' + editForm.classId}
                              {editForm.sectionId && ` - Section ${editForm.sectionId}`}
                            </>
                          ) : 'Not assigned'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="form-section" style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#FF8C42', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>G) School Transport Service</h4>
                    <div className="transport-service-toggle-container">
                      <label className="transport-service-label" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                        <span>Enable Transport Service</span>
                        <div className="toggle-switch" style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
                          <input
                            type="checkbox"
                            id="editTransportServiceEnabled"
                            checked={editForm.transportServiceEnabled}
                            onChange={(e) => {
                              handleEditInputChange('transportServiceEnabled', e.target.checked);
                              if (!e.target.checked) {
                                handleEditInputChange('pickupLocationId', '');
                                handleEditInputChange('useStudentAddressAsPickup', false);
                                handleEditInputChange('transportRouteType', '');
                                handleEditInputChange('transportTime', '');
                                handleEditInputChange('routeId', '');
                              }
                            }}
                            style={{ opacity: 0, width: 0, height: 0 }}
                          />
                          <span className="toggle-slider" style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: editForm.transportServiceEnabled ? '#FF8C42' : '#ccc',
                            transition: '0.3s',
                            borderRadius: '24px'
                          }}>
                            <span style={{
                              position: 'absolute',
                              content: '""',
                              height: '18px',
                              width: '18px',
                              left: '3px',
                              bottom: '3px',
                              backgroundColor: 'white',
                              transition: '0.3s',
                              borderRadius: '50%',
                              transform: editForm.transportServiceEnabled ? 'translateX(26px)' : 'translateX(0)'
                            }} />
                          </span>
                        </div>
                      </label>
                      <p className="transport-service-help" style={{ color: '#6B7280', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                        {editForm.transportServiceEnabled 
                          ? 'Transport service is enabled for this student.' 
                          : 'Transport service is disabled for this student.'}
                      </p>
                      
                      {editForm.transportServiceEnabled && (
                        <div className="form-group" style={{ marginTop: '1.5rem' }}>
                          <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                id="editUseStudentAddressAsPickup"
                                checked={editForm.useStudentAddressAsPickup}
                                onChange={(e) => {
                                  handleEditInputChange('useStudentAddressAsPickup', e.target.checked);
                                  if (e.target.checked) {
                                    handleEditInputChange('pickupLocationId', '');
                                    handleEditInputChange('routeId', '');
                                  }
                                }}
                                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#FF8C42' }}
                              />
                              <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
                                Use Student Address as Pickup Location
                              </span>
                            </label>
                          </div>
                          
                          {/* Route Type Selection */}
                          <div className="form-group" style={{ marginTop: '1rem' }}>
                            <label htmlFor="editTransportRouteType" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.95rem', fontWeight: '600', color: '#374151' }}>
                              Select Route Type (Optional)
                            </label>
                            <select
                              id="editTransportRouteType"
                              value={editForm.transportRouteType}
                              onChange={(e) => {
                                handleEditInputChange('transportRouteType', e.target.value);
                                handleEditInputChange('transportTime', e.target.value !== '1 Way' ? '' : editForm.transportTime);
                                handleEditInputChange('routeId', '');
                              }}
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #D1D5DB',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                backgroundColor: '#FFFFFF',
                                cursor: 'pointer'
                              }}
                            >
                              <option value="">-- Select route type (Optional) --</option>
                              <option value="1 Way">1 Way</option>
                              <option value="2 Way">2 Way</option>
                            </select>
                          </div>
                          
                          {/* Morning/Evening Selection for 1 Way */}
                          {editForm.transportRouteType === '1 Way' && (
                            <div className="form-group" style={{ marginTop: '1rem' }}>
                              <label htmlFor="editTransportTime" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.95rem', fontWeight: '600', color: '#374151' }}>
                                Select Time (Optional)
                              </label>
                              <select
                                id="editTransportTime"
                                value={editForm.transportTime}
                                onChange={(e) => handleEditInputChange('transportTime', e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: '8px',
                                  fontSize: '0.95rem',
                                  backgroundColor: '#FFFFFF',
                                  cursor: 'pointer'
                                }}
                              >
                                <option value="">-- Select time (Optional) --</option>
                                <option value="Morning">Morning</option>
                                <option value="Evening">Evening</option>
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-section" style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#FF8C42', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>Fees Information</h4>
                    <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #E5E7EB' }}>
                      <div className="payment-method-section" style={{ marginTop: 0, paddingTop: 0, border: 'none' }}>
                        <label className="payment-method-label" style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#2c3e50', fontSize: '0.95rem' }}>Payment Method *</label>
                        <div className="payment-method-options" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          <label className="payment-option" style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.75rem', 
                            padding: '0.75rem', 
                            background: editForm.paymentMethod === 'Cash' ? '#FFF4ED' : '#FFFFFF', 
                            border: editForm.paymentMethod === 'Cash' ? '1px solid #FF8C42' : '1px solid #e9ecef', 
                            borderRadius: '6px', 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (editForm.paymentMethod !== 'Cash') {
                              e.currentTarget.style.borderColor = '#FF8C42';
                              e.currentTarget.style.background = '#FFF4ED';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (editForm.paymentMethod !== 'Cash') {
                              e.currentTarget.style.borderColor = '#e9ecef';
                              e.currentTarget.style.background = '#FFFFFF';
                            }
                          }}>
                            <input
                              type="radio"
                              name="editPaymentMethod"
                              value="Cash"
                              checked={editForm.paymentMethod === 'Cash'}
                              onChange={(e) => handleEditInputChange('paymentMethod', e.target.value)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#FF8C42' }}
                            />
                            <span style={{ color: editForm.paymentMethod === 'Cash' ? '#FF8C42' : '#2c3e50', fontSize: '0.9rem', fontWeight: editForm.paymentMethod === 'Cash' ? '600' : '400' }}>a. Cash</span>
                          </label>
                          <label className="payment-option" style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.75rem', 
                            padding: '0.75rem', 
                            background: editForm.paymentMethod === 'Cheque' ? '#FFF4ED' : '#FFFFFF', 
                            border: editForm.paymentMethod === 'Cheque' ? '1px solid #FF8C42' : '1px solid #e9ecef', 
                            borderRadius: '6px', 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (editForm.paymentMethod !== 'Cheque') {
                              e.currentTarget.style.borderColor = '#FF8C42';
                              e.currentTarget.style.background = '#FFF4ED';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (editForm.paymentMethod !== 'Cheque') {
                              e.currentTarget.style.borderColor = '#e9ecef';
                              e.currentTarget.style.background = '#FFFFFF';
                            }
                          }}>
                            <input
                              type="radio"
                              name="editPaymentMethod"
                              value="Cheque"
                              checked={editForm.paymentMethod === 'Cheque'}
                              onChange={(e) => handleEditInputChange('paymentMethod', e.target.value)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#FF8C42' }}
                            />
                            <span style={{ color: editForm.paymentMethod === 'Cheque' ? '#FF8C42' : '#2c3e50', fontSize: '0.9rem', fontWeight: editForm.paymentMethod === 'Cheque' ? '600' : '400' }}>b. Cheque</span>
                          </label>
                          <label className="payment-option" style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.75rem', 
                            padding: '0.75rem', 
                            background: editForm.paymentMethod === 'Mobile money' ? '#FFF4ED' : '#FFFFFF', 
                            border: editForm.paymentMethod === 'Mobile money' ? '1px solid #FF8C42' : '1px solid #e9ecef', 
                            borderRadius: '6px', 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (editForm.paymentMethod !== 'Mobile money') {
                              e.currentTarget.style.borderColor = '#FF8C42';
                              e.currentTarget.style.background = '#FFF4ED';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (editForm.paymentMethod !== 'Mobile money') {
                              e.currentTarget.style.borderColor = '#e9ecef';
                              e.currentTarget.style.background = '#FFFFFF';
                            }
                          }}>
                            <input
                              type="radio"
                              name="editPaymentMethod"
                              value="Mobile money"
                              checked={editForm.paymentMethod === 'Mobile money'}
                              onChange={(e) => handleEditInputChange('paymentMethod', e.target.value)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#FF8C42' }}
                            />
                            <span style={{ color: editForm.paymentMethod === 'Mobile money' ? '#FF8C42' : '#2c3e50', fontSize: '0.9rem', fontWeight: editForm.paymentMethod === 'Mobile money' ? '600' : '400' }}>c. Any other (Mobile money)</span>
                          </label>
                        </div>
                        {editErrors.paymentMethod && (
                          <span className="error-text" style={{ color: '#EF4444', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>{editErrors.paymentMethod}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Documents Section */}
                  <div className="form-section" style={{ marginBottom: '2rem' }}>
                    <h4 style={{ color: '#FF8C42', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>I) DOCUMENTS</h4>
                    <div className="documents-content" style={{ padding: '1rem', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      <div className="document-upload-section" style={{ marginBottom: '1.5rem' }}>
                        <div className="document-upload-controls" style={{ marginBottom: '1rem' }}>
                          <div>
                            <label htmlFor="editDocumentTypeSelect" className="document-type-label">Document Type</label>
                            <select
                              id="editDocumentTypeSelect"
                              className="document-type-select"
                              defaultValue=""
                              style={{ width: '100%', padding: '0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                            >
                              <option value="">Select Document Type</option>
                              <option value="Birth Certificate">Birth Certificate</option>
                              <option value="Parent National ID">Parent National ID</option>
                              <option value="Parent Passport">Parent Passport</option>
                              <option value="Academic Transcripts">Academic Transcripts</option>
                              <option value="Previous School Certificate">Previous School Certificate</option>
                              <option value="Medical Certificate">Medical Certificate</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <input
                            type="file"
                            id="editDocumentFileInput"
                            className="document-file-input"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              const typeSelect = document.getElementById('editDocumentTypeSelect');
                              const type = typeSelect.value;
                              if (file && type) {
                                handleEditDocumentAdd(type, file);
                                e.target.value = '';
                                typeSelect.value = '';
                              } else if (file && !type) {
                                alert('Please select a document type first');
                                e.target.value = '';
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="add-document-btn"
                            onClick={() => {
                              const typeSelect = document.getElementById('editDocumentTypeSelect');
                              const type = typeSelect.value;
                              if (!type) {
                                alert('Please select a document type first');
                                return;
                              }
                              document.getElementById('editDocumentFileInput').click();
                            }}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: '#FF8C42',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              alignSelf: 'flex-end'
                            }}
                          >
                            + Add Document
                          </button>
                        </div>
                      </div>
                      
                      {/* Existing Documents */}
                      {editForm.documents && editForm.documents.filter(doc => !doc.parent_id).length > 0 && (
                        <div className="documents-list" style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: '#374151' }}>Existing Documents ({editForm.documents.filter(doc => !doc.parent_id).length})</h4>
                          {editForm.documents.filter(doc => !doc.parent_id).map((doc, index) => (
                            <div key={doc.id || index} className="document-item" style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '0.75rem',
                              background: '#FFFFFF',
                              borderRadius: '6px',
                              border: '1px solid #E5E7EB',
                              marginBottom: '0.5rem'
                            }}>
                              <div className="document-info" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                                <span className="document-type-badge" style={{ 
                                  padding: '0.25rem 0.75rem', 
                                  background: '#FF8C42', 
                                  color: 'white', 
                                  borderRadius: '4px',
                                  fontSize: '0.85rem',
                                  fontWeight: '500'
                                }}>{doc.document_type}</span>
                                <a 
                                  href={doc.document_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="document-name"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    const newUrl = await getValidFileUrl(doc.document_url);
                                    if (newUrl) {
                                      window.open(newUrl, '_blank');
                                    } else {
                                      alert('Unable to access document. Please try again or contact support.');
                                    }
                                  }}
                                  style={{ 
                                    color: '#FF8C42', 
                                    textDecoration: 'none',
                                    fontWeight: '500',
                                    fontSize: '0.9rem'
                                  }}
                                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                >
                                  {doc.file_name || 'View Document'} →
                                </a>
                                {doc.file_size && (
                                  <span className="document-size" style={{ fontSize: '0.85rem', color: '#6B7280' }}>
                                    ({(doc.file_size / 1024 / 1024).toFixed(2)} MB)
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="remove-document-btn"
                                onClick={() => handleEditDocumentRemove(index, false)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#EF4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* New Documents */}
                      {editForm.newDocuments && editForm.newDocuments.length > 0 && (
                        <div className="documents-list" style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600', color: '#374151' }}>New Documents to Upload ({editForm.newDocuments.length})</h4>
                          {editForm.newDocuments.map((doc, index) => (
                            <div key={`new-${index}`} className="document-item" style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '0.75rem',
                              background: '#FFFFFF',
                              borderRadius: '6px',
                              border: '1px solid #E5E7EB',
                              marginBottom: '0.5rem'
                            }}>
                              <div className="document-info" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                                <select
                                  value={doc.type}
                                  onChange={(e) => handleEditDocumentTypeChange(index, e.target.value, true)}
                                  className="document-type-edit"
                                  style={{ 
                                    padding: '0.5rem', 
                                    border: '1px solid #D1D5DB', 
                                    borderRadius: '6px',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  <option value="Birth Certificate">Birth Certificate</option>
                                  <option value="Parent National ID">Parent National ID</option>
                                  <option value="Parent Passport">Parent Passport</option>
                                  <option value="Academic Transcripts">Academic Transcripts</option>
                                  <option value="Previous School Certificate">Previous School Certificate</option>
                                  <option value="Medical Certificate">Medical Certificate</option>
                                  <option value="Other">Other</option>
                                </select>
                                <span className="document-name" style={{ fontSize: '0.9rem', color: '#374151', fontWeight: '500' }}>{doc.file.name}</span>
                                <span className="document-size" style={{ fontSize: '0.85rem', color: '#6B7280' }}>
                                  ({(doc.file.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                              </div>
                              <button
                                type="button"
                                className="remove-document-btn"
                                onClick={() => handleEditDocumentRemove(index, true)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#EF4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {(!editForm.documents || editForm.documents.length === 0) && 
                       (!editForm.newDocuments || editForm.newDocuments.length === 0) && (
                        <p className="no-documents-text" style={{ color: '#6B7280', fontSize: '0.9rem', textAlign: 'left', padding: '1rem 0' }}>
                          No documents uploaded yet.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="form-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #E5E7EB' }}>
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={closeEditPopup}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#F3F4F6',
                        color: '#374151',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmittingEdit}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: isSubmittingEdit ? '#9CA3AF' : '#FF8C42',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        cursor: isSubmittingEdit ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isSubmittingEdit ? 'Updating...' : 'Update Student'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation Popup */}
          {showDeleteConfirmPopup && studentToDelete && (
            <div 
              className="popup-overlay" 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                animation: 'fadeIn 0.3s ease-in'
              }}
              onClick={cancelDelete}
            >
              <div 
                style={{
                  background: '#FFFFFF',
                  borderRadius: '20px',
                  padding: '3rem',
                  maxWidth: '500px',
                  width: '90%',
                  textAlign: 'center',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  animation: 'slideUp 0.4s ease-out',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                  zIndex: 0
                }}></div>
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  left: '-30px',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                  zIndex: 0
                }}></div>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
                  position: 'relative',
                  zIndex: 1,
                  animation: 'scaleIn 0.5s ease-out'
                }}>
                  <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>🗑️</span>
                </div>
                <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
                  Delete Student?
                </h2>
                <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
                  Are you sure you want to permanently delete <strong>{studentToDelete.first_name} {studentToDelete.middle_name ? studentToDelete.middle_name + ' ' : ''}{studentToDelete.last_name}</strong>?
                  <br /><br />
                  This will:
                  <br />• Remove the student from the database completely
                  <br />• Delete their login account (if exists)
                  <br /><strong style={{ color: '#EF4444' }}>This action CANNOT be undone!</strong>
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                  <button onClick={cancelDelete} disabled={isDeleting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isDeleting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isDeleting ? 0.5 : 1 }}>
                    Cancel
                  </button>
                  <button onClick={confirmDelete} disabled={isDeleting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isDeleting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isDeleting ? 0.5 : 1, boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)' }}>
                    {isDeleting ? 'Deleting...' : 'Delete Student'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Success Popup */}
          {showDeleteSuccessPopup && (
            <div 
              className="popup-overlay" 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                animation: 'fadeIn 0.3s ease-in'
              }}
              onClick={() => setShowDeleteSuccessPopup(false)}
            >
              <div 
                style={{
                  background: '#FFFFFF',
                  borderRadius: '20px',
                  padding: '3rem',
                  maxWidth: '500px',
                  width: '90%',
                  textAlign: 'center',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  animation: 'slideUp 0.4s ease-out',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                  zIndex: 0
                }}></div>
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  left: '-30px',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                  zIndex: 0
                }}></div>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
                  position: 'relative',
                  zIndex: 1,
                  animation: 'scaleIn 0.5s ease-out'
                }}>
                  <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
                </div>
                <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
                  Student Deleted Successfully! ✅
                </h2>
                <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
                  <strong>{deletedStudentName}</strong> has been permanently deleted from the system.
                </p>
                <button onClick={() => setShowDeleteSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)' }}>
                  OK
                </button>
              </div>
            </div>
          )}

          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideUp {
              from { transform: translateY(30px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
            @keyframes scaleIn {
              from { transform: scale(0); opacity: 0; }
              to { transform: scale(1); opacity: 1; }
            }
          `}</style>
        </div>
      </div>
    </div>
  );
}

export default ReceptionistDashboard;
